/**
 * Arena — Phase 2 MVP. Pick a CPU or boss, fight, watch the battle log
 * replay turn-by-turn, collect Battle Coins + feed item drops + XP.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { playSfx, sfxForLogEntry, isMuted, toggleMuted } from "@/utils/battleSfx";
import { banterFromLogEntry, pickBanter, getPetIntro } from "@/utils/battleBanter";
import WeeklyArenaPanel from "@/components/WeeklyArenaPanel";
import ArenaMarketPanel from "@/components/ArenaMarketPanel";
import SoulKeyPanel from "@/components/SoulKeyPanel";
import { IntroCutscene, UltimateCutscene, PostFightCinema } from "@/components/ArenaCinematics";
import HeroSkillBar from "@/components/HeroSkillBar";
import SkillPicker from "@/components/combat/SkillPicker";
import AdminHeroSwitcher from "@/components/admin/AdminHeroSwitcher";
import WorldBossLeaderboard from "@/components/WorldBossLeaderboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURN_DELAY_MS = 480;  // pacing for log reveal

// 🎨 Per-boss arena stage themes — gradient + atmospheric overlay class.
// Overlay classes (`arena-bg-*`) are defined in App.css and add fog,
// embers, lightning, etc on top of the gradient.
const DEFAULT_STAGE = {
  background: "linear-gradient(180deg, #1a0d2b 0%, #0F0F0F 50%, #1a0500 100%)",
  overlay: null,
  floorColor: "rgba(212,175,55,0.4)",
};
const BOSS_STAGES = {
  "Greyfang": {
    // Misty grey forest
    background: "linear-gradient(180deg, #1F2937 0%, #111827 60%, #030712 100%)",
    overlay: "arena-bg-mist",
    floorColor: "rgba(148,163,184,0.5)",
  },
  "Sting Pharaoh": {
    // Desert dunes at dusk
    background: "linear-gradient(180deg, #92400E 0%, #7C2D12 45%, #1C1917 100%)",
    overlay: "arena-bg-sand",
    floorColor: "rgba(217,119,6,0.55)",
  },
  "Drakar the Burning": {
    // Magma cracks
    background: "radial-gradient(circle at 50% 80%, #DC2626 0%, #7C2D12 25%, #1C0A07 70%, #000 100%)",
    overlay: "arena-bg-embers",
    floorColor: "rgba(220,38,38,0.7)",
  },
  "Stormwing": {
    // Stormy sky with lightning
    background: "linear-gradient(180deg, #1E293B 0%, #0F172A 40%, #020617 100%)",
    overlay: "arena-bg-storm",
    floorColor: "rgba(37,99,235,0.55)",
  },
  "The Unrisen": {
    // Purple ash temple
    background: "radial-gradient(ellipse at 50% 60%, #581C87 0%, #2E1065 35%, #0F0028 70%, #000 100%)",
    overlay: "arena-bg-ash",
    floorColor: "rgba(124,58,237,0.7)",
  },
};

export default function Arena() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fight, setFight] = useState(null);     // server result
  const [revealed, setRevealed] = useState([]); // log lines drip-revealed
  const [hpPet, setHpPet] = useState(null);
  const [hpCpu, setHpCpu] = useState(null);
  // 💠 Mana Bubble state — surfaced on the battlefield HUD (Mar 2026)
  // so players can see their resource mid-fight without leaving the arena.
  const [manaPet, setManaPet] = useState(0);
  const [manaCapPet, setManaCapPet] = useState(100);
  const [finished, setFinished] = useState(false);
  const [boostPrompt, setBoostPrompt] = useState(null); // {boss} when user clicked a boss
  const [awaitingMove, setAwaitingMove] = useState(false); // 🎮 player's turn to pick
  const [pvpState, setPvpState] = useState(null);
  const [pvpOpponents, setPvpOpponents] = useState([]);
  const [rebirthDummies, setRebirthDummies] = useState([]);   // 🥊 daily Phoenix Rebirth Dummy roster
  const [floatingHeals, setFloatingHeals] = useState([]);     // 🍓 floating "+80 HP" numbers
  // 🌍👹 Feb 10, 2026 — World-boss shared spawn state, polled every 15s.
  const [worldBossStates, setWorldBossStates] = useState({}); // { boss_id: {hp_current, hp_max, raider_count, top_contributors[]} }
  const [worldBossKills, setWorldBossKills]   = useState([]); // recent kills for the broadcast ticker
  const [announcedKillIds, setAnnouncedKillIds] = useState(() => new Set());
  const replayTimer = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/arena`);
        if (!cancelled) setState(r.data);
      } catch (e) {
        console.error(e);
      }
      // ⚔️ PvP state — loads separately so a PvP failure doesn't break Arena
      try {
        const r2 = await axios.get(`${API}/pvp/state`);
        if (!cancelled) setPvpState(r2.data);
      } catch (e) {
        console.error(e);
      }
      // 🥊 Phoenix Rebirth Dummies — load asynchronously, fail-soft
      try {
        const r3 = await axios.get(`${API}/arena/rebirth-dummies`);
        if (!cancelled) setRebirthDummies(r3.data?.roster || []);
      } catch (e) {
        console.error("rebirth dummies load:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // 🌍👹 Feb 10, 2026 — Poll world-boss shared spawn state + recent
  // kills. Runs every 15s so raider counts + community HP stay
  // reasonably fresh without hammering the server.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [states, kills] = await Promise.all([
          axios.get(`${API}/arena/world-boss/states`),
          axios.get(`${API}/arena/world-boss/recent-kills`),
        ]);
        if (cancelled) return;
        setWorldBossStates(states.data?.spawns || {});
        const newKills = kills.data?.kills || [];
        setWorldBossKills(newKills);
        // 👑 Killing-Blow Broadcast — announce any kill we haven't
        // seen before as a site-wide toast.
        setAnnouncedKillIds((prev) => {
          const next = new Set(prev);
          newKills.forEach((k) => {
            if (!prev.has(k.spawn_id) && prev.size > 0) {
              // Skip the very first fetch to avoid spamming a fresh
              // load with historical kills.
              toast.success(
                `👑 ${k.killer_name || "A raider"} felled ${k.boss_icon || ""} ${k.boss_name}!`,
                { duration: 8000 },
              );
            }
            next.add(k.spawn_id);
          });
          return next;
        });
      } catch (e) {
        console.error("world boss poll:", e);
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/arena`);
      setState(r.data);
    } catch {}
    try {
      const r2 = await axios.get(`${API}/pvp/state`);
      setPvpState(r2.data);
    } catch {}
    try {
      const r3 = await axios.get(`${API}/arena/rebirth-dummies`);
      setRebirthDummies(r3.data?.roster || []);
    } catch {}
  };

  // ⚔️ PvP Quick Match — find 5 opponents, pick one, start fight
  const findPvpOpponents = async () => {
    try {
      const r = await axios.get(`${API}/pvp/opponents`);
      setPvpOpponents(r.data.opponents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load opponents.");
    }
  };

  const startPvpFight = async (target) => {
    if (busy) return;
    setBusy(true);
    setFight(null);
    setRevealed([]);
    setFinished(false);
    try {
      const r = await axios.post(`${API}/pvp/start`, {
        target_user_id: target.user_id,
      });
      setFight({
        ...r.data,
        battle_log: [],
        outcome: null,
        win: false,
        draw: false,
      });
      setHpPet(r.data.pet_stats.hp ?? r.data.pet_stats.max_hp);
      setHpCpu(r.data.cpu_stats.max_hp);
      setManaPet(r.data.state?.mana ?? 0);
      setManaCapPet(r.data.state?.mana_cap ?? 100);
      setAwaitingMove(true);
      setPvpOpponents([]); // close the picker
      // 🔊 Phoenix vs Phoenix — swing intro
      playSfx("swing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start PvP fight.");
      setBusy(false);
    }
  };

  const startFight = async (opponent, isBoss, boost = null) => {
    if (busy) return;
    setBusy(true);
    setBoostPrompt(null);
    setFight(null);
    setRevealed([]);
    setFinished(false);
    try {
      // 🎮 Open an interactive fight — server seeds state, we drive turns.
      const r = await axios.post(`${API}/arena/start`, {
        opponent_id: opponent.id,
        is_boss: !!isBoss,
        boost,
      });
      // Build a "fight" object compatible with the existing overlay UI.
      // Final rewards arrive later from /arena/turn → setFight() again.
      setFight({
        ...r.data,
        // start_fight returns pet_stats, cpu_stats, fight_id, is_boss
        battle_log: [],
        status_effects: r.data.state?.status_effects || {pet: [], cpu: []},
        outcome: null,
        win: false,
        draw: false,
      });
      setHpPet(r.data.pet_stats.hp ?? r.data.pet_stats.max_hp);
      setHpCpu(r.data.cpu_stats.max_hp);
      setManaPet(r.data.state?.mana ?? 0);
      setManaCapPet(r.data.state?.mana_cap ?? 100);
      setAwaitingMove(true); // player picks the first move
      // 🔊 Boss fights start with a roar — sets the tone
      if (isBoss) playSfx("boss_roar");
      else playSfx("swing");
      // 🔊 Battle music loop — WORLD/WILD bosses use the dedicated
      // Dragonious boss bopper (owner spec Feb 2026); everyone else
      // gets the standard battle bopper.
      window.dispatchEvent(new Event(isBoss ? "battle-audio:start-boss" : "battle-audio:start"));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't start fight.";
      toast.error(msg);
      setBusy(false);
    }
  };

  // 🎮 Player picks a move for the current turn → POST /arena/turn → drip
  // the new log entries, then either re-open the picker or finalize.
  const submitMove = async (move) => {
    if (!fight || !fight.fight_id || finished) return;
    setAwaitingMove(false);
    // Accept either a legacy string ("heavy"/"light"/"defend") or a rich
    // skill object `{move: "skill", skill_id}` from the SkillPicker.
    let body = { fight_id: fight.fight_id };
    if (typeof move === "object" && move !== null) {
      body.move = move.move;
      if (move.skill_id) body.skill_id = move.skill_id;
    } else {
      body.move = move;
    }
    try {
      const r = await axios.post(`${API}/arena/turn`, body);
      const newEntries = r.data.new_log_entries || [];
      // 🌶️ Capture latest status_effects from /turn response so pills update live
      if (r.data.state?.status_effects) {
        setFight((prev) => prev ? {...prev, status_effects: r.data.state.status_effects} : prev);
      }
      // 💠 Mana sync — /arena/turn resolves skills through hero_skill_executor
      // (Generator gain / Active cost), so the authoritative mana lives on
      // r.data.state.mana. Without this the SkillPicker's mana bar stays
      // stuck at 0 no matter how many Generators fire. (Fixed Sep 2026.)
      if (typeof r.data.state?.mana === "number") setManaPet(r.data.state.mana);
      if (typeof r.data.state?.mana_cap === "number") setManaCapPet(r.data.state.mana_cap);
      // 🌍👹 Feb 12, 2026 — World Boss killing-blow trophy payout.
      // Fires ONCE per player, right after the turn that dropped the
      // shared HP pool to 0. Owner spec: "1 congratulations and no
      // more" — a single toast per player, no ticker, no replay.
      if (r.data.world_boss_trophy_payout) {
        const payout = r.data.world_boss_trophy_payout;
        const my = (payout.trophies_paid || []).find(
          (t) => t.user_id === user?.user_id,
        );
        if (my) {
          const parts = [];
          const rw = my.reward || {};
          if (rw.nests) parts.push(`${rw.nests} 🪹 Nests`);
          if (rw.chits) parts.push(`${rw.chits} 🪙 Chits`);
          const tierLabel = my.tier === "killing_blow"
            ? "👑 KILLING BLOW"
            : my.tier === "podium"
              ? `🥈 Rank ${my.rank}`
              : `🏅 Rank ${my.rank}`;
          toast.success(
            `${tierLabel} — congratulations! You earned ${parts.join(" + ")}.`,
            { duration: 8000, id: `wb-trophy-${payout.spawn_id}` },
          );
        }
      }
      // Drip-feed new entries (usually 1-3 per turn) at TURN_DELAY_MS spacing
      newEntries.forEach((line, idx) => {
        const t = setTimeout(() => {
          setRevealed((prev) => [...prev, line]);
          if (typeof line.pet_hp === "number") setHpPet(line.pet_hp);
          if (typeof line.cpu_hp === "number") setHpCpu(line.cpu_hp);
          // 🔊 Fire matching SFX for each revealed line
          const sfx = sfxForLogEntry(line);
          if (sfx) playSfx(sfx);
        }, idx * TURN_DELAY_MS);
        replayTimer.current = t;
      });
      // After all entries play, either ask for next move or finalize
      const tailDelay = newEntries.length * TURN_DELAY_MS;
      const t2 = setTimeout(() => {
        if (r.data.finished && r.data.result) {
          // 🏆 Final state — merge full result into fight so result panel renders
          setFight((prev) => ({
            ...prev,
            ...r.data.result,
            // make sure pet_stats keeps the strategy if any was applied later
          }));
          setHpPet(r.data.result.pet_hp_final);
          setHpCpu(r.data.result.cpu_hp_final);
          setFinished(true);
          // 🔊 Result fanfare
          const o = r.data.result.outcome;
          if (o === "pet") playSfx("victory");
          else if (o === "cpu") playSfx("defeat");
          // 🔊 Battle audio — victory fanfare on win, silence on defeat/draw.
          if (o === "pet") window.dispatchEvent(new Event("battle-audio:victory"));
          else window.dispatchEvent(new Event("battle-audio:defeat"));
        } else if (r.data.finished && !r.data.result) {
          // 🩹 Aug 2026 fallback — server reported finished but skipped the
          // result payload (concurrent raid kill / stale finalize). Instead
          // of freezing the picker, mark the fight done and close it out.
          // Rewards for THIS user are handled server-side; if none, that's
          // the raid's decision.
          toast.warning("Raid resolved elsewhere — closing out this fight.");
          setFinished(true);
          setTimeout(() => closeFight(), 800);
        } else {
          setAwaitingMove(true);
        }
      }, Math.max(120, tailDelay));
      replayTimer.current = t2;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't submit move.";
      toast.error(msg);
      setAwaitingMove(true);
    }
  };

  // 🦸⚔️ Phase 2 — Hero Skill via /api/arena/use-skill (instant resolve, single-target damage + effects).
  // Skills are FREE actions on top of move picking. They mutate state (HP, mana, effects)
  // but don't burn a turn. After cast, the player still picks a basic move.
  const submitSkill = async (skill) => {
    if (!fight || !fight.fight_id || finished) return null;
    try {
      const r = await axios.post(`${API}/arena/use-skill`, {
        fight_id: fight.fight_id,
        skill_id: skill.id,
      });
      // Merge updated state — HP, mana, status_effects all returned
      setFight((prev) => prev ? {
        ...prev,
        status_effects: r.data.state?.status_effects || prev.status_effects,
        mana: r.data.state?.mana,
        mana_cap: r.data.state?.mana_cap,
      } : prev);
      if (typeof r.data.state?.cpu_hp === "number") setHpCpu(r.data.state.cpu_hp);
      if (typeof r.data.state?.pet_hp === "number") setHpPet(r.data.state.pet_hp);
      // 💠 Mana Bubble sync — hero_skill_executor mutates state.mana on
      // every skill use; keep the HUD in lock-step with it.
      if (typeof r.data.state?.mana === "number") setManaPet(r.data.state.mana);
      if (typeof r.data.state?.mana_cap === "number") setManaCapPet(r.data.state.mana_cap);
      // Pump a synthetic log entry so the player sees what just happened.
      const summaryEntry = {
        turn: r.data.state?.turn || 0,
        type: "hero_skill",
        actor: "pet",
        icon: skill.icon,
        name: r.data.skill_name,
        dmg: r.data.dmg,
        mana_after: r.data.state?.mana,
        pet_hp: r.data.state?.pet_hp,
        cpu_hp: r.data.state?.cpu_hp,
        msg: `${skill.icon || "✨"} ${r.data.skill_name}${r.data.dmg ? ` — ${r.data.dmg} dmg` : ""}`,
      };
      setRevealed((prev) => [...prev, summaryEntry]);
      const sfx = skill.kind === "ultimate" ? "ultimate" : "hit_heavy";
      try { playSfx(sfx); } catch { /* sfx may not exist */ }

      // If the skill KO'd the enemy → finalize immediately (server returned finished:true)
      if (r.data.finished && r.data.winner === "pet") {
        playSfx("victory");
        window.dispatchEvent(new Event("battle-audio:victory"));
        // Pull final result from server via abandon → it auto-finalizes. Simpler: just flag finished.
        setFinished(true);
        setFight((prev) => prev ? {...prev, outcome: "pet", win: true} : prev);
      }
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skill failed.");
      return null;
    }
  };

  // Boss click → ONLY raid bosses (timed-window with start_hour) get the
  // 🔮 charm prompt. Always-on bosses (no start_hour) fight raw — no charm
  // applies to them anyway. We don't gate on `busy` here because opening
  // the prompt is safe — the actual /arena/start call inside startFight()
  // handles concurrency. Gating here caused "lit-up boss doesn't open"
  // bugs when `busy` stayed true from a prior interrupted fight.
  const onBossClick = (boss) => {
    if (busy && !fight) setBusy(false);
    const isRaidBoss = boss?.start_hour !== null && boss?.start_hour !== undefined;
    if (isRaidBoss) {
      setBoostPrompt({ boss });
    } else {
      // Always-on boss → straight to fight, no charm option.
      startFight(boss, true, null);
    }
  };

  // When finished, refresh the side panel state and unlock the button
  useEffect(() => {
    if (finished) {
      refresh();
      setBusy(false);
    }
  }, [finished]);

  // 🍓 Embersweet Fruit Leather — buy + use
  const buyRedLeather = async (currency) => {
    try {
      await axios.post(`${API}/arena/buy-red-leather`, { currency });
      toast.success(`🍓 +1 Embersweet Fruit Leather (-${currency === "pt" ? "50 Phoenix Tokens" : "5 Nests"})`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't buy Embersweet Fruit Leather.");
    }
  };

  const useRedLeather = async () => {
    // Capture pre-heal HP so we can compute the actual heal amount.
    const preHp = state?.stats?.hp ?? 0;
    try {
      const r = await axios.post(`${API}/pet/use-red-leather`);
      const postHp = r.data?.current_hp ?? 0;
      const healed = Math.max(0, postHp - preHp);
      if (healed > 0) {
        // 🍓 Pop a floating "+N HP" number above the HP bar — fades in 1.2s
        const fid = Date.now() + Math.random();
        setFloatingHeals((prev) => [...prev, { id: fid, amount: healed }]);
        setTimeout(() => {
          setFloatingHeals((prev) => prev.filter((f) => f.id !== fid));
        }, 1300);
      }
      toast.success(`🍓 Pet restored to ${postHp} HP`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't use Embersweet Fruit Leather.");
    }
  };

  const closeFight = () => {
    setFight(null);
    setRevealed([]);
    setFinished(false);
    setAwaitingMove(false);
    setBusy(false);  // 🐛 Fix: previously busy stayed true after flee → all Fight buttons stayed greyed
    if (replayTimer.current) clearTimeout(replayTimer.current);
    // 🔊 Silence battle audio when the overlay closes.
    window.dispatchEvent(new Event("battle-audio:stop"));
  };

  // 🏳️ Flee — abandon the current fight: reverts rewards, breaks streak,
  // closes the overlay immediately. Available mid-replay or post-result.
  const fleeFight = async () => {
    if (!fight) return;
    if (replayTimer.current) clearTimeout(replayTimer.current);
    try {
      if (finished) {
        // Post-result flee on a completed CPU fight → revert rewards
        await axios.post(`${API}/arena/flee`, { fight_id: fight.fight_id });
        toast.warning("🏳️ Fled the fight — rewards reverted, streak broken.");
      } else {
        // Mid-fight abandon → server marks abandoned, no rewards granted
        await axios.post(`${API}/arena/abandon`, { fight_id: fight.fight_id });
        toast.warning("🏳️ Abandoned the fight — daily slot consumed, streak broken.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't flee.";
      toast.error(msg);
    }
    closeFight();
    refresh();
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="arena-locked">
        <div className="font-display text-3xl mb-2 text-white">🛡️ Fiery Phoenix Arena</div>
        <div>Sign in and hatch a Phoenix Egg to step into the ring.</div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500" data-testid="arena-loading">
        Loading the arena…
      </div>
    );
  }

  const pet = state.pet;
  const stats = state.pet_battle_stats;
  const fightsLeft = state.fights_remaining;

  return (
    <div className="bg-[#080808] min-h-screen text-white" data-testid="arena-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">Phase 2 · Fiery Phoenix Arena</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">🛡️ Step Into The Ring</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-xl mx-auto">
            Train your Phoenix Pet against CPU dummies and bosses. Win battle coins, feed drops, and a growing streak. Hungry pets fight at <span className="text-[#DC2626] font-bold">half power</span> — keep them fed.
          </p>
          {/* 🛠️ Owner-only class switcher — bypasses V1 no-swap so we can
              live-test SkillPicker + kits across every hero. Renders null
              for non-owners. */}
          <div className="mt-3 flex justify-center">
            <AdminHeroSwitcher onSwitched={() => window.location.reload()} />
          </div>
        </header>

        {/* 🩹 Oct 2026 (Fix #6) — Battlegrounds had no entry point from
            the Arena hub; was only reachable via direct URL. Pin it right
            alongside the end-game doors so it's discoverable. */}
        <div className="mb-6" data-testid="arena-battlegrounds-strip">
          <Link to="/battlegrounds" data-testid="arena-battlegrounds-link" className="block">
            <div className="relative bg-gradient-to-br from-[#0A0A0A] to-black border border-sky-500 p-5 overflow-hidden text-center transition hover:brightness-110"
                 style={{ boxShadow: "inset 0 0 60px #0ea5e922, 0 0 24px #0ea5e955" }}>
              <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/70 border border-sky-500/60 text-sky-400 text-[9px] uppercase tracking-widest font-bold">
                🔓 Open
              </div>
              <div className="text-4xl mb-2">⚔️</div>
              <div className="font-display text-xl tracking-[0.35em] text-sky-400 mb-1" style={{ textShadow: "0 0 12px #0ea5e944" }}>
                BATTLEGROUNDS
              </div>
              <p className="text-[11px] italic text-neutral-400 leading-relaxed max-w-[36ch] mx-auto mb-1">
                10v10 PvP · haul shards, hunt guards, bank the win. Weekly honor ladder.
              </p>
              <div className="text-[9px] uppercase tracking-widest text-white font-bold">
                Join the warmup →
              </div>
            </div>
          </Link>
        </div>

        {/* 🔓👻 End-Game Arenas — BOTH DOORS OPEN (Roc ruling 2026-10-03).
            Overrides the prior gating plan. No hero-level, no pet-tier
            requirements. The doors and the pages agree: both open, enter
            at will. */}
        <div className="mb-8" data-testid="arena-endgame-strip">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold text-center mb-2">
            🔓 Two end-game doors — both open
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/phoenix-rush" data-testid="arena-tile-phoenix-rush-link" className="block">
              <EndgameArenaTile
                testId="arena-tile-phoenix-rush"
                glyph="🔥"
                codeName="Phoenix Rush"
                tease="A hallway that never stops. Something moves the second the last thing stops moving."
                unlockHint="Enter the gauntlet 🔥"
                accent="#F97316"
                unlocked={true}
              />
            </Link>
            <Link to="/phantom-nest" data-testid="arena-tile-phantom-nest-link" className="block">
              <EndgameArenaTile
                testId="arena-tile-phantom-nest"
                glyph="👻"
                codeName="Phantom Nest"
                tease="Every day, four familiar shadows dress up as somebody else."
                unlockHint="Step inside tonight 👻"
                accent="#A78BFA"
                unlocked={true}
              />
            </Link>
          </div>
        </div>

        {/* Top status bar */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-8">
          <StatTile label="Battle Coins" value={state.battle_coins} icon="🪙" testId="arena-coins" />
          <StatTile label="Fights Left Today" value={`${fightsLeft}/${state.free_fights_per_day}`} icon="⚔️" testId="arena-fights-left" />
          <StatTile label="Win Streak" value={`${state.win_streak} · ${state.streak_multiplier}x`} icon="🔥" testId="arena-streak" />
          <StatTile label="Best Streak" value={state.best_streak} icon="🏆" testId="arena-best-streak" />
          <StatTile label="Killing Blows" value={state.total_killing_blows || 0} icon="💥" testId="arena-killing-blows" />
        </div>

        {/* Pet card */}
        <div data-testid="arena-pet-card" className="bg-[#0F0F0F] border-2 border-[#D4AF37]/40 p-5 mb-8">
          <div className="flex items-center gap-4">
            <div className="text-6xl">{stats.icon}</div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Your Fighter</div>
              <div className="font-display text-2xl">{stats.name}</div>
              <div className="text-xs text-neutral-400">{stats.tier_label} · <span data-testid="arena-pet-mood">{stats.mood_label}</span></div>
            </div>
            {/* 🍓 Embersweet Fruit Leather inventory + use */}
            <div className="text-right" data-testid="arena-red-leather-panel">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Embersweet</div>
              <div className="font-mono tabular-nums text-2xl text-[#DC2626]" data-testid="arena-red-leather-count">
                🍓 {state.red_leather || 0}
              </div>
              <button
                onClick={useRedLeather}
                disabled={(state.red_leather || 0) < 1 || stats.hp >= stats.max_hp || busy}
                data-testid="arena-use-red-leather"
                className="mt-1 text-[10px] uppercase tracking-widest bg-[#DC2626] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-2 py-1"
                title="Restore your pet to full HP"
              >
                Restore HP
              </button>
            </div>
          </div>
          {/* HP bar — persists between fights */}
          <div className="mt-3 relative" data-testid="arena-pet-hp-bar">
            {/* 🍓 Floating "+N HP" numbers fly upward over the HP bar */}
            {floatingHeals.map((f) => (
              <div
                key={f.id}
                data-testid={`arena-float-heal-${f.id}`}
                className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 text-2xl font-black text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)] z-10 select-none"
                style={{ animation: "embersweet-float 1.2s ease-out forwards" }}
              >
                +{f.amount} 🍓
              </div>
            ))}
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              <span>HP</span>
              <span className="font-mono tabular-nums text-[#22C55E]">{stats.hp} / {stats.max_hp}</span>
            </div>
            <div className="h-2 bg-black border border-zinc-800 overflow-hidden" style={{ boxSizing: "border-box" }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  // Ticket 4 fix: clamp pct 0..100 to prevent under/overfill; box-sizing:border-box on container so inner width:100% reaches full visible width.
                  width: `${Math.min(100, Math.max(0, Math.round((stats.hp / (stats.max_hp || 1)) * 100)))}%`,
                  background: stats.hp <= 0 ? "#7C2D12" : "#22C55E",
                }}
              />
            </div>
            {stats.hp < stats.max_hp && state.hp_regen && (
              <RegenTicker meta={state.hp_regen} onTick={refresh} />
            )}
            {stats.hp <= 0 && (
              <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold mt-2 text-center" data-testid="arena-pet-gassed">
                🩸 Pet gassed out — use a 🍓 Embersweet Fruit Leather (or rebirth) before fighting again.
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4 text-center">
            <StatPill label="HP"    value={stats.max_hp} color="#22C55E" />
            <StatPill label="ATK"   value={stats.atk}    color="#DC2626" />
            <StatPill label="DEF"   value={stats.def}    color="#2563EB" />
            <StatPill label="SPD"   value={stats.spd}    color="#D4AF37" />
            <StatPill label="CRIT%" value={stats.crit}   color="#A855F7" />
            <StatPill
              label="BLOCK%"
              value={stats.block ?? 0}
              color="#38BDF8"
              data-testid="arena-stat-block"
            />
          </div>
          {pet && !pet.alive && (
            <div className="mt-3 text-[11px] uppercase tracking-widest text-[#DC2626] font-bold text-center">
              💀 In ash form — rebirth your pet at <a href="/pet" className="underline">/pet</a> before fighting.
            </div>
          )}
        </div>

        {/* ☯️ Oct 2026 — Soul Key panel pulled up to the top of the Arena.
            Previously it lived WAY below the Arena Market, so Roc couldn't
            find it. Keeping it right under the fighter stats panel so the
            "change my hero" flow is always one scroll away. */}
        <SoulKeyPanel />

{/* 🍓 Embersweet Fruit Leather — STORE REMOVED Feb 2026 per owner steer.
            Players still earn Leathers from the Guild Lever wheel (55% drop), and
            can still USE them from the inventory chip above. Just no buy-buttons here.

            If owner wants to fully retire Embersweet at some point, the chip + use
            button (around line 443) and `buyRedLeather()` handler (around line 328)
            can also be deleted — but that's a separate decision. */}

        {/* 🏆 Weekly Arena Rewards */}
        <WeeklyArenaPanel />

        {/* ⚔️ Arena Market — Battle Coins sink */}
        <ArenaMarketPanel />

        {/* ⚔️ PvP Quick Match */}
        <section className="mb-8" data-testid="arena-pvp-section">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display text-2xl">⚔️ PvP Quick Match</h2>
            {pvpState && (
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                {pvpState.fights_today}/{pvpState.free_pvp_fights_per_day} fights today · W/L {pvpState.pvp_wins}/{pvpState.pvp_losses}
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Hunt other phoenixes. Tier ±1 only. Win: <span className="text-[#FCD34D]">+{pvpState?.win_pt || 20} 🔥 PT</span> + <span className="text-[#D4AF37]">+{pvpState?.win_coins || 50} 🪙</span>. Loss: <span className="text-neutral-400">+{pvpState?.loss_pt || 5} Phoenix Tokens consolation</span>.
          </p>
          {pvpOpponents.length === 0 ? (
            <button
              onClick={findPvpOpponents}
              disabled={busy || (pvpState && pvpState.fights_remaining <= 0)}
              data-testid="pvp-find-btn"
              className="w-full bg-gradient-to-r from-[#7C3AED] to-[#DC2626] hover:from-[#6D28D9] hover:to-[#B91C1C] disabled:opacity-40 text-white font-bold uppercase tracking-widest py-3 transition"
            >
              {pvpState && pvpState.fights_remaining <= 0 ? "🔒 Daily PvP cap reached" : "🔍 Find Opponents"}
            </button>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pvp-opponent-list">
              {pvpOpponents.map((o) => (
                <PvpOpponentCard
                  key={o.user_id}
                  opp={o}
                  disabled={busy}
                  onChallenge={() => startPvpFight(o)}
                />
              ))}
            </div>
          )}
          {pvpOpponents.length > 0 && (
            <button
              onClick={() => setPvpOpponents([])}
              data-testid="pvp-close-btn"
              className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
            >
              ✕ Close opponent list
            </button>
          )}
        </section>

        {/* 🤖 CPU Dummies — RETIRED Feb 2026 per Roc. Section now hides
            automatically since arena_service.CPU_DUMMIES is empty. Phoenix
            Rebirth Dummies (mixed into PvP candidate pool) + raid bosses
            cover all AI opponent slots. */}
        {(state.cpu_opponents || []).length > 0 && (
          <section className="mb-10" data-testid="arena-cpu-section">
            <h2 className="font-display text-2xl mb-1">🤖 CPU Dummies</h2>
            <p className="text-xs text-neutral-500 mb-4">Practice fights. Earn coins. Some carry leaked future-season gear 👀</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {state.cpu_opponents.map((c) => (
                <OpponentCard
                  key={c.id}
                  opp={c}
                  disabled={busy || fightsLeft <= 0 || !(pet?.alive) || stats.hp <= 0}
                  onFight={() => startFight(c, false)}
                  accent="#D4AF37"
                />
              ))}
            </div>
          </section>
        )}

        {/* 🥊 Phoenix Rebirth Dummies — RETIRED Feb 2026 as a standalone
            section. They now seed the PvP candidate pool (see
            pvp_service.find_opponents top-up) so PvP feels populated 24/7.
            The card component below + the /api/arena/rebirth-dummies
            endpoint stay live for admin/debug. */}
        {false && rebirthDummies.length > 0 && (
          <section className="mb-10" data-testid="arena-rebirth-dummies-section">
            <h2 className="font-display text-2xl mb-1">🥊 Phoenix Rebirth Dummies</h2>
            <p className="text-xs text-neutral-500 mb-4">
              Random hero opponents — every class, every rebirth tier.
              Class-locked skills, random armor, scaled to the real Phoenix Rebirth math.
              Win for <span className="text-amber-400 font-bold">Battle Coins + Brawler Spirit</span>,
              lose and Dave hands you <span className="text-purple-300 font-bold">5 💀 Life Ash</span> on the way out.
              <span className="text-neutral-600"> · Roster refreshes daily</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {rebirthDummies.map((d) => (
                <RebirthDummyCard
                  key={d.id}
                  dummy={d}
                  disabled={busy || fightsLeft <= 0 || !(pet?.alive) || stats.hp <= 0}
                  onFight={() => startFight(d, false)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Bosses */}
        <section className="mb-10" data-testid="arena-boss-section">
          <h2 className="font-display text-2xl mb-1">🐉 Boss Raids</h2>
          {/* 👑 Feb 10, 2026 — Killing-Blow broadcast ticker.
              Shows the last few world-boss kills within 24h. */}
          {worldBossKills.length > 0 && (
            <div
              className="border border-[#D4AF37]/40 bg-black/60 px-3 py-1.5 mb-3 flex items-center gap-3 overflow-hidden"
              data-testid="arena-killing-blow-ticker"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold shrink-0">
                👑 Recent Kills
              </span>
              <div className="flex-1 flex items-center gap-4 overflow-x-auto text-[11px]">
                {worldBossKills.slice(0, 5).map((k) => (
                  <span
                    key={k.spawn_id}
                    className="whitespace-nowrap text-neutral-300"
                    data-testid={`arena-recent-kill-${k.boss_id}`}
                  >
                    <span className="text-[#FCD34D] font-bold">{k.killer_name || "Someone"}</span>
                    <span className="text-neutral-500"> felled </span>
                    <span className="text-white font-bold">{k.boss_icon || "🐉"} {k.boss_name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-neutral-500 mb-2">
            Endgame fights with massive HP pools. <span className="text-emerald-400 font-bold">Chits-only grind</span> — win pays chits (per-damage + rank), killing blow pays <span className="text-[#FCD34D] font-bold">20,000 chits</span> every time. <span className="text-neutral-600">No XP, no eggs, no items — pet must grind XP from CPU dummies.</span> <span className="text-[#FCD34D]">After 5 attacks the boss charges an ULTIMATE</span> — KO them in 30s or eat a knockout (pet doesn't die, just sleeps 30s).
          </p>
          {(() => {
            const openBoss = state.bosses.find((b) => b.start_hour != null && b.is_available_now);
            if (openBoss) {
              return (
                <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold mb-4" data-testid="arena-time-window">
                  🟢 LIVE NOW: {openBoss.name} · {openBoss.window_label}
                </p>
              );
            }
            // Find the boss opening soonest among always-on labels
            const nextBoss = [...state.bosses]
              .filter((b) => b.start_hour != null && typeof b.seconds_until_open === "number" && b.seconds_until_open > 0)
              .sort((a, b) => a.seconds_until_open - b.seconds_until_open)[0];
            if (nextBoss) {
              const s = nextBoss.seconds_until_open;
              const h = Math.floor(s / 3600);
              const m = Math.floor((s % 3600) / 60);
              const eta = h > 0 ? `${h}h ${m}m` : `${m}m`;
              return (
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#FCD34D] font-bold mb-4" data-testid="arena-time-window">
                  ⏰ Next boss window: {nextBoss.name} · opens in {eta}
                </p>
              );
            }
            return null;
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {state.bosses.map((b) => (
              <OpponentCard
                key={b.id}
                opp={b}
                isBoss
                locked={b.start_hour != null && !b.is_available_now}
                lockedLabel={b.window_label}
                secondsUntilOpen={b.seconds_until_open}
                disabled={busy || !(pet?.alive) || stats.hp <= 0 || (b.start_hour != null && !b.is_available_now) || (b.raid && b.raid.status !== "alive")}
                onFight={() => onBossClick(b)}
                accent={b.color || "#DC2626"}
                worldBossState={worldBossStates[b.id]}
              />
            ))}
          </div>
        </section>

        {/* Streak rewards reference */}
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 text-center max-w-md mx-auto">
          🔥 Streak multipliers: 2w · 1.2x · 3w · 1.5x · 5w · 2.0x · 10w · 3.0x · 20w · 5.0x — resets on loss.
        </div>
      </div>

      {/* Boss boost prompt */}
      {boostPrompt && (
        <BoostPromptOverlay
          boss={boostPrompt.boss}
          boost={state.raid_charm || state.power_boost}
          onCancel={() => setBoostPrompt(null)}
          onFight={(boostType) => startFight(boostPrompt.boss, true, boostType)}
        />
      )}

      {/* Fight modal */}
      {fight && (
        <FightOverlay
          fight={fight}
          revealed={revealed}
          hpPet={hpPet}
          hpCpu={hpCpu}
          manaPet={manaPet}
          manaCapPet={manaCapPet}
          finished={finished}
          awaitingMove={awaitingMove}
          onMove={submitMove}
          onSkill={submitSkill}
          onClose={closeFight}
          onFlee={fleeFight}
          onGlimmerFired={(payload) => {
            // 🩹 Feb 2026 — sync HP bar immediately after Nirvana / Mend
            // (previously the bar stayed stuck until the next attack tick).
            if (typeof payload.pet_hp === "number") setHpPet(payload.pet_hp);
            if (typeof payload.cpu_hp === "number") setHpCpu(payload.cpu_hp);
            if (payload.heal_amount && payload.heal_amount > 0) {
              const fid = Date.now() + Math.random();
              setFloatingHeals((prev) => [...prev, { id: fid, amount: payload.heal_amount }]);
              setTimeout(() => setFloatingHeals((prev) => prev.filter((h) => h.id !== fid)), 1400);
            }
          }}
          onKoSkipped={(entries) => {
            // 💤⚡ Feb 10, 2026 — Push the "wake" log entry into revealed
            // so isKO flips false and the HeroSkillBar re-appears.
            (entries || []).forEach((line) => {
              setRevealed((prev) => [...prev, line]);
              if (typeof line.pet_hp === "number") setHpPet(line.pet_hp);
              if (typeof line.cpu_hp === "number") setHpCpu(line.cpu_hp);
            });
            setAwaitingMove(true);
          }}
          worldBossState={fight?.is_boss ? worldBossStates[fight.opponent_id] : null}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, icon, testId }) {
  // 🩹 Feb 2026 owner fix (#16): render "0" when value is null/undefined
  // so labels never appear valueless.
  const display = (value === null || value === undefined || value === "") ? 0 : value;
  return (
    <div className="bg-[#0A0A0A] border border-zinc-900 p-3 text-center" data-testid={testId}>
      <div className="text-2xl leading-none mb-1">{icon}</div>
      <div className="font-mono tabular-nums text-xl text-white">{display}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

// 🔒 EndgameArenaTile — REDACTED coming-soon card for the end-game
// arenas. Owner directive Mar 2026: "I want almost everything to be a
// self discovery." No mechanics, no bullets, no proper name — just a
// silhouette + cryptic tease + level gate. Full reveal only happens
// when the tile unlocks in-game.
// Canon: /app/memory/PHOENIX_RUSH_AND_PHANTOM_NEST_END_ARENAS_FEB2026.md
function EndgameArenaTile({ testId, glyph, codeName, tease, unlockHint, accent, unlocked = false }) {
  return (
    <div
      data-testid={testId}
      className="relative bg-gradient-to-br from-[#0A0A0A] to-black border p-5 overflow-hidden text-center transition hover:brightness-110"
      style={{
        borderColor: unlocked ? accent : `${accent}44`,
        boxShadow: unlocked ? `inset 0 0 60px ${accent}22, 0 0 24px ${accent}55` : `inset 0 0 60px ${accent}0A`,
      }}
    >
      {/* Subtle glitch overlay so it feels intentionally hidden, not just empty */}
      {!unlocked && (
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 3px)",
          }}
        />
      )}
      <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/70 border text-[9px] uppercase tracking-widest font-bold"
           style={{ borderColor: `${accent}66`, color: accent }}>
        {unlocked ? "🔓 Open" : "🔒 Locked"}
      </div>
      <div
        className="text-5xl leading-none mb-3 select-none"
        style={{
          filter: unlocked ? "none" : "blur(1px) grayscale(0.4) contrast(1.2)",
          opacity: unlocked ? 1 : 0.55,
          textShadow: `0 0 22px ${accent}66`,
        }}
      >
        {glyph}
      </div>
      <div
        className="font-display text-xl tracking-[0.35em] mb-1"
        style={{ color: accent, textShadow: `0 0 12px ${accent}44` }}
      >
        {codeName}
      </div>
      <p className="text-[11px] italic text-neutral-400 leading-relaxed max-w-[28ch] mx-auto mb-2">
        {tease}
      </p>
      <div className={`text-[9px] uppercase tracking-widest ${unlocked ? "text-white font-bold" : "text-neutral-600"}`}>
        {unlockHint}
      </div>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="bg-[#080808] border border-zinc-900 py-1.5" style={{ borderTopColor: color, borderTopWidth: 2 }}>
      <div className="font-mono tabular-nums text-base text-white" style={{ color }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

/** 🥊 Phoenix Rebirth Dummy card — class identity + tier rank + skill loadout preview. */
function RebirthDummyCard({ dummy, disabled, onFight }) {
  const stats = dummy.preview_stats || {};
  const accent = dummy.class_color || "#A855F7";
  const tierColor = dummy.tier_color || "#D4AF37";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onFight}
      disabled={disabled}
      data-testid={`rebirth-dummy-${dummy.id}`}
      className={`relative text-left bg-[#0a0a0a] border-2 p-3 transition-all flex flex-col gap-2 rounded-sm ${
        disabled
          ? "border-zinc-800 opacity-60 cursor-not-allowed"
          : "border-zinc-800 hover:bg-[#111] cursor-pointer hover:scale-[1.02]"
      }`}
      style={!disabled ? { borderColor: `${accent}55`, boxShadow: `0 0 12px ${accent}22` } : undefined}
    >
      {/* Tier rank chip — top right */}
      <div
        className="absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-sm"
        style={{ background: `${tierColor}22`, color: tierColor, border: `1px solid ${tierColor}66` }}
      >
        {dummy.tier_icon} R{dummy.rebirth_tier} · {dummy.short_name}
      </div>

      {/* Class header */}
      <div className="flex items-center gap-2">
        <div className="text-2xl drop-shadow-[0_0_6px_currentColor]" style={{ color: accent }}>
          {dummy.class_icon}
        </div>
        <div className="flex-1 min-w-0 pr-14">
          <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: accent }}>
            {dummy.class_name}
          </div>
          <div className="text-sm font-black text-white truncate leading-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            {dummy.name}
          </div>
        </div>
      </div>

      {/* Tagline */}
      <div className="text-[10px] italic text-neutral-400 leading-snug min-h-[26px]">
        "{dummy.tagline}"
      </div>

      {/* Armor */}
      <div className="flex items-center gap-1 text-[10px]">
        <span className="text-neutral-500">🛡️</span>
        <span className="text-neutral-300 font-bold">{dummy.armor?.name}</span>
        <span className="text-neutral-600 uppercase">· {dummy.armor?.rarity}</span>
      </div>

      {/* Skill loadout */}
      <div className="text-[9px] text-neutral-500 leading-snug">
        <span className="uppercase tracking-wider text-neutral-600 font-bold">Loadout:</span>{" "}
        {(dummy.skill_loadout || []).slice(0, 4).join(" · ")}
      </div>

      {/* Stats row + rebirth bonus chip */}
      <div className="flex items-center justify-between gap-1 pt-1 border-t border-zinc-900">
        <div className="text-[10px] tabular-nums text-neutral-400">
          <span className="text-rose-400">HP</span> {stats.hp}{" "}
          <span className="text-amber-400">ATK</span> {stats.atk}{" "}
          <span className="text-cyan-400">DEF</span> {stats.def}
        </div>
        <div
          className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded-sm"
          style={{ background: `${tierColor}22`, color: tierColor }}
          title="Phoenix Rebirth permanent stat bonus baked in"
        >
          +{stats.rebirth_bonus_pct}%
        </div>
      </div>
    </button>
  );
}



function OpponentCard({ opp, isBoss, disabled, onFight, accent, locked, lockedLabel, secondsUntilOpen, worldBossState }) {
  // ⏰ 1-hour-window bosses get a live countdown ("Opens in 3h 12m")
  const countdownText = useMemo(() => {
    if (!locked || !secondsUntilOpen || secondsUntilOpen <= 0) return null;
    const s = secondsUntilOpen;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `Opens in ${h}h ${m}m`;
    return `Opens in ${m}m`;
  }, [locked, secondsUntilOpen]);
  return (
    <div
      data-testid={`arena-${isBoss ? "boss" : "cpu"}-${opp.id}`}
      data-locked={locked ? "true" : "false"}
      className={`bg-[#0F0F0F] border p-3 flex flex-col text-center transition hover:bg-[#141414] ${locked ? "opacity-60" : ""}`}
      style={{ borderColor: accent + (isBoss ? "AA" : "55") }}
    >
      <div className="text-5xl mb-1" style={locked ? { filter: "grayscale(80%) brightness(0.6)" } : undefined}>
        {opp.icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: accent }}>
        {opp.difficulty_label}
      </div>
      <div className="font-display text-base leading-tight mb-1">{opp.name}</div>
      {opp.subtitle && <div className="text-[10px] uppercase tracking-widest text-neutral-500">{opp.subtitle}</div>}
      <div className="text-[10px] text-neutral-400 italic mb-2 px-1 leading-snug">{opp.tagline}</div>
      {isBoss && (
        <>
          <div className="text-[10px] font-mono tabular-nums text-[#DC2626] mb-1">HP {opp.hp_preview}</div>
          <div className="text-[9px] uppercase tracking-widest text-[#FCD34D] mb-1">🌋 Has Ultimate</div>
        </>
      )}
      <div className="text-[10px] font-mono tabular-nums text-[#D4AF37] mb-2">
        Win: +{opp.coins_reward} 🪙{isBoss ? ` · 🥛` : ""}
      </div>
      {opp.has_preview_leak && (
        <div className="text-[9px] uppercase tracking-widest text-cyan-400 mb-2">👀 Carries leaked gear</div>
      )}
      {/* When unlocked: show the boss's 1-hour window so user knows they're inside it.
          🩹 Oct 2026 (Fix #30): hide LIVE when the raid is already killed/expired
          so the card can't say "LIVE" while the pre-fight modal says "already slain". */}
      {isBoss && opp.window_label && !locked &&
        !(opp.raid && (opp.raid.status === "killed" || opp.raid.status === "expired")) && (
        <div className="text-[9px] uppercase tracking-[0.15em] text-emerald-400 mb-2 leading-tight font-bold" data-testid={`arena-boss-open-${opp.id}`}>
          🟢 LIVE · {opp.window_label}
        </div>
      )}
      {/* Slain / Bled Out indicator — replace the stale LIVE badge. */}
      {isBoss && opp.raid && opp.raid.status === "killed" && (
        <div className="text-[9px] uppercase tracking-[0.15em] text-red-400 mb-2 leading-tight font-bold" data-testid={`arena-boss-slain-${opp.id}`}>
          💀 SLAIN · {opp.window_label || "this window"}
        </div>
      )}
      {isBoss && opp.raid && opp.raid.status === "expired" && (
        <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 mb-2 leading-tight font-bold" data-testid={`arena-boss-bled-${opp.id}`}>
          🪦 BLED OUT · window closed
        </div>
      )}
      {/* 🐉 RAID HP — shared pool across all players this hour */}
      {isBoss && opp.raid && (
        <RaidPanel raid={opp.raid} bossId={opp.id} bossColor={accent} />
      )}
      {/* 🌍 Feb 10, 2026 — Community HP + raider count strip.
          Only renders when the world-boss spawn is live and the
          RaidPanel isn't already covering the same info. */}
      {isBoss && worldBossState && !opp.raid && (
        <div
          className="border border-emerald-500/40 bg-emerald-950/25 p-1.5 mb-2 leading-tight"
          data-testid={`arena-world-boss-strip-${opp.id}`}
        >
          <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-emerald-300 font-bold">
            <span>🌍 Community</span>
            <span data-testid={`arena-raider-count-${opp.id}`}>
              {worldBossState.raider_count} raider{worldBossState.raider_count === 1 ? "" : "s"}
            </span>
          </div>
          <div className="text-[10px] font-mono tabular-nums text-emerald-200 mt-0.5">
            {worldBossState.hp_current.toLocaleString()}/{worldBossState.hp_max.toLocaleString()}
          </div>
          <div className="h-1 bg-neutral-900 border border-neutral-700 mt-0.5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-[width] duration-500"
              style={{ width: `${Math.max(0, Math.min(100, (worldBossState.hp_current / Math.max(1, worldBossState.hp_max)) * 100))}%` }}
            />
          </div>
        </div>
      )}
      {/* 🏆 Feb 2026 — Live top-10 damage leaderboard + projected trophy
         preview. Fires on both raid and non-raid boss cards so grinders
         can see their rank + projected reward in real time. */}
      {isBoss && worldBossState && (
        <WorldBossLeaderboard bossId={opp.id} state={worldBossState} />
      )}
      {/* 🔮 User's Power Charm level for THIS raid window */}
      {isBoss && opp.my_raid_charm && opp.my_raid_charm.level > 0 && (
        <div
          className="bg-purple-950/60 border border-[#7C3AED] text-[#C4B5FD] text-[10px] uppercase tracking-[0.15em] font-bold py-1 mb-2 leading-tight"
          data-testid={`arena-boss-charm-${opp.id}`}
          title={`Your Power Charm for this raid: +${opp.my_raid_charm.pct}% all stats`}
        >
          🔮 Charm Lv {opp.my_raid_charm.level} / {opp.my_raid_charm.max_level} · +{opp.my_raid_charm.pct}%
        </div>
      )}
      {locked && lockedLabel && (
        <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 mb-1 leading-tight" data-testid={`arena-boss-locked-${opp.id}`}>
          🔒 {lockedLabel}
        </div>
      )}
      {countdownText && (
        <div className="text-[9px] uppercase tracking-[0.2em] text-[#FCD34D] mb-2 font-bold tabular-nums" data-testid={`arena-boss-countdown-${opp.id}`}>
          ⏳ {countdownText}
        </div>
      )}
      <button
        onClick={onFight}
        disabled={disabled}
        data-testid={`arena-fight-btn-${opp.id}`}
        className="mt-auto px-3 py-1.5 text-xs uppercase tracking-widest font-bold disabled:opacity-40"
        style={{ background: locked ? "#3F3F46" : accent, color: locked ? "#A1A1AA" : "#000" }}
      >
        {locked ? "Locked" : (isBoss && opp.raid && opp.raid.status === "killed") ? "Slain" : (isBoss && opp.raid && opp.raid.status === "expired") ? "Bled Out" : "Fight"}
      </button>
    </div>
  );
}

function FightOverlay({ fight, revealed, hpPet, hpCpu, manaPet = 0, manaCapPet = 100, finished, awaitingMove, onMove, onSkill, onClose, onFlee, onGlimmerFired, onKoSkipped, worldBossState }) {
  const pet = fight.pet_stats;
  const cpu = fight.cpu_stats;
  const pctPet = Math.max(0, Math.round(((hpPet ?? pet.max_hp) / pet.max_hp) * 100));
  const pctCpu = Math.max(0, Math.round(((hpCpu ?? cpu.max_hp) / cpu.max_hp) * 100));

  // ⚔️ Floating combat numbers — emit red "−N" on damage taken,
  // emerald "+N" on heals. Watch HP changes on each side independently.
  const [petFloats, setPetFloats] = useState([]);
  const [cpuFloats, setCpuFloats] = useState([]);
  const prevPetHpRef = useRef(hpPet);
  const prevCpuHpRef = useRef(hpCpu);
  useEffect(() => {
    const prev = prevPetHpRef.current;
    if (typeof prev === "number" && typeof hpPet === "number" && prev !== hpPet) {
      const delta = hpPet - prev;
      const fid = Date.now() + Math.random();
      setPetFloats((f) => [...f, { id: fid, delta }]);
      setTimeout(() => setPetFloats((f) => f.filter((x) => x.id !== fid)), 1100);
    }
    prevPetHpRef.current = hpPet;
  }, [hpPet]);
  useEffect(() => {
    const prev = prevCpuHpRef.current;
    if (typeof prev === "number" && typeof hpCpu === "number" && prev !== hpCpu) {
      const delta = hpCpu - prev;
      const fid = Date.now() + Math.random();
      setCpuFloats((f) => [...f, { id: fid, delta }]);
      setTimeout(() => setCpuFloats((f) => f.filter((x) => x.id !== fid)), 1100);
    }
    prevCpuHpRef.current = hpCpu;
  }, [hpCpu]);

  // 🎨 Boss-specific arena stage — themed gradient + atmospheric overlay
  const isBossFight = !!cpu.is_boss;
  const bossStage = isBossFight ? BOSS_STAGES[cpu.name] || DEFAULT_STAGE : DEFAULT_STAGE;

  // 🎬 Drive sprite animations from the LATEST revealed log line.
  // Resets each time a new entry is appended so animations re-fire.
  const last = revealed[revealed.length - 1] || null;
  const lastIdx = revealed.length - 1;
  const petHit = last && last.type === "attack" && last.actor === "cpu";
  const cpuHit = last && last.type === "attack" && last.actor === "pet";
  const petAttacking = last && last.type === "attack" && last.actor === "pet";
  const cpuAttacking = last && last.type === "attack" && last.actor === "cpu";
  const ultimate = last && last.type === "ultimate";
  const isCrit = last && last.crit;
  const isCharging = last && last.type === "charging";
  const isKO = last && last.type === "ko";
  // 💫 Stagger animations — apply_to_target tells us WHO just got staggered.
  // The log's `actor` is the attacker; the target is the opposite.
  const petStaggered = last && last.type === "stagger_apply" && last.actor === "cpu"; // pet was staggered by cpu's heavy hit
  const cpuStaggered = last && last.type === "stagger_apply" && last.actor === "pet"; // cpu was staggered by pet's heavy hit
  const petStaggerSkip = last && last.type === "stagger_skip" && last.actor === "pet";
  const cpuStaggerSkip = last && last.type === "stagger_skip" && last.actor === "cpu";
  const lastDmg = last && (last.dmg || 0);

  // 🗨️ Banter — compute pet/cpu lines from the latest log entry, plus
  // injected intro/result banter on first reveal and on fight end.
  const petPersonality = "balanced";  // player pet uses balanced bank
  const cpuPersonality = (fight?.cpu_stats?.personality) || "balanced";
  const [petBanter, setPetBanter] = useState(null);
  const [cpuBanter, setCpuBanter] = useState(null);
  const [petBanterKey, setPetBanterKey] = useState(0);
  const [cpuBanterKey, setCpuBanterKey] = useState(0);
  const lastBanterIdxRef = useRef(-1);

  // 🎬 Cinematic phase state — drives intro cutscene (first 2.2s) +
  // post-fight cinema overlays. Re-keyed by fight_id so each new fight
  // plays fresh.
  const [introDone, setIntroDone] = useState(false);
  const [postFightActive, setPostFightActive] = useState(false);
  useEffect(() => {
    // Reset cinematic state every new fight
    setIntroDone(false);
    setPostFightActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fight?.fight_id]);
  useEffect(() => {
    if (finished && !postFightActive) setPostFightActive(true);
  }, [finished, postFightActive]);
  // Extract ultimate move name from log entry's text (boss ult name often
  // embedded in log message — fallback to generic).
  const ultimateMoveName = ultimate && last?.text
    ? (last.text.match(/uses?\s+([A-Z][^!.\n]{2,40})/i)?.[1] || "ULTIMATE STRIKE").toUpperCase()
    : "ULTIMATE STRIKE";

  // 💤⚡ Feb 10, 2026 — KO cooldown: greyscale the stage, run a
  // personal 30s countdown, expose a "5 OD skip" button.
  const [koSecs, setKoSecs] = useState(0);
  const [koSkipBusy, setKoSkipBusy] = useState(false);
  const koActive = !!isKO;
  useEffect(() => {
    if (!koActive) { setKoSecs(0); return; }
    setKoSecs(30);
    const id = setInterval(() => {
      setKoSecs((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [koActive, last?.turn]);
  const onSkipKo = async () => {
    if (!fight?.fight_id || koSkipBusy) return;
    setKoSkipBusy(true);
    try {
      const res = await axios.post(
        `${API}/arena/ko/skip`,
        { fight_id: fight.fight_id },
      );
      if (res.data?.skipped) {
        toast.success(`⚡ Cooldown skipped — ${res.data.od_spent} OD spent.`);
        setKoSecs(0);
        // Push the "wake" log entry into the revealed feed so isKO
        // flips false and the HeroSkillBar re-appears for the next move.
        const entries = res.data.new_log_entries || [];
        onKoSkipped?.(entries);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip cooldown.");
    } finally {
      setKoSkipBusy(false);
    }
  };

  // Intro banter on the first log line
  useEffect(() => {
    if (!fight) return;
    if (revealed.length !== 0) return;
    const tier = fight?.pet_stats?.tier;
    setPetBanter(getPetIntro(tier));
    setCpuBanter(pickBanter(cpuPersonality, "intro"));
    setPetBanterKey((k) => k + 1);
    setCpuBanterKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fight?.fight_id]);

  // Per-turn banter from last entry
  useEffect(() => {
    if (!last) return;
    if (lastIdx === lastBanterIdxRef.current) return;
    lastBanterIdxRef.current = lastIdx;
    const petLine = banterFromLogEntry(last, "pet", petPersonality, fight?.pet_stats?.tier);
    const cpuLine = banterFromLogEntry(last, "cpu", cpuPersonality, null);
    if (petLine) { setPetBanter(petLine); setPetBanterKey((k) => k + 1); }
    if (cpuLine) { setCpuBanter(cpuLine); setCpuBanterKey((k) => k + 1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastIdx]);

  // Final result banter
  useEffect(() => {
    if (!finished || !fight?.outcome) return;
    if (fight.outcome === "pet") {
      setPetBanter(pickBanter(petPersonality, "win"));
      setCpuBanter(pickBanter(cpuPersonality, "loss"));
    } else if (fight.outcome === "cpu") {
      setPetBanter(pickBanter(petPersonality, "loss"));
      setCpuBanter(pickBanter(cpuPersonality, "win"));
    }
    setPetBanterKey((k) => k + 1);
    setCpuBanterKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, fight?.outcome]);

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-3 ${
        ultimate ? "arena-screen-flash" : ""
      }`}
      data-testid="arena-fight-overlay"
    >
      <div className={`bg-[#080808] border-2 ${ultimate ? "border-[#DC2626]" : "border-[#D4AF37]"} max-w-3xl w-full max-h-[92vh] overflow-y-auto ${ultimate ? "arena-screen-shake" : ""}`}>

        {/* 🎬 BATTLE STAGE — large sprites + HP bars + damage numbers */}
        <div
          className="relative overflow-hidden border-b-2 border-[#D4AF37]/30"
          style={{
            minHeight: 220,
            background: bossStage.background,
            // 💤 Feb 10, 2026 — Greyscale the stage while pet is KO'd.
            filter: koActive ? "grayscale(1) brightness(0.55)" : "none",
            transition: "filter 300ms ease",
          }}
          data-testid={koActive ? "arena-stage-ko" : "arena-stage"}
        >
          {/* Atmospheric overlay (fog/embers/sparks per boss) */}
          {bossStage.overlay && (
            <div
              aria-hidden
              className={`absolute inset-0 pointer-events-none ${bossStage.overlay}`}
            />
          )}
          {/* Subtle ring floor */}
          <div
            aria-hidden
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] h-12 opacity-40"
            style={{
              background: `radial-gradient(ellipse at center, ${bossStage.floorColor} 0%, transparent 70%)`,
            }}
          />
          <div className="grid grid-cols-2 gap-2 p-4 relative">
            <FighterStage
              stats={pet}
              hp={hpPet ?? pet.max_hp}
              pct={pctPet}
              side="left"
              color="#D4AF37"
              hit={petHit}
              attacking={petAttacking}
              crit={isCrit && petHit}
              ko={isKO}
              ultimateIncoming={ultimate}
              staggered={petStaggered}
              staggeredSkip={petStaggerSkip}
              dmg={petHit ? lastDmg : 0}
              animKey={`pet-${lastIdx}`}
              banter={petBanter}
              banterKey={petBanterKey}
              charmLevel={Number(pet?.raid_charm_level || 0)}
              charmPct={Number(pet?.raid_charm_pct || 0)}
              statusEffects={fight?.status_effects?.pet || []}
              floats={petFloats}
            />
            <FighterStage
              stats={cpu}
              hp={hpCpu ?? cpu.max_hp}
              pct={pctCpu}
              side="right"
              color={cpu.color || "#DC2626"}
              hit={cpuHit}
              attacking={cpuAttacking}
              crit={isCrit && cpuHit}
              charging={isCharging}
              ultimateUsed={ultimate}
              staggered={cpuStaggered}
              staggeredSkip={cpuStaggerSkip}
              dmg={cpuHit ? lastDmg : 0}
              animKey={`cpu-${lastIdx}`}
              banter={cpuBanter}
              banterKey={cpuBanterKey}
              statusEffects={fight?.status_effects?.cpu || []}
              floats={cpuFloats}
            />
          </div>
          {/* 💠 Battlefield Mana Bar — Mar 2026 (owner directive).
              Slim, always-visible mana readout under the fighter stages so
              players know their bubble state without opening the picker.
              Only renders on the pet side because the CPU doesn't spend mana. */}
          <div
            className="px-4 pb-2 -mt-1"
            data-testid="arena-battlefield-mana"
          >
            <div className="flex items-center justify-between text-[9px] uppercase tracking-widest mb-0.5">
              <span className="text-cyan-300/70 font-bold">💠 Mana</span>
              <span className="tabular-nums text-cyan-300 font-bold" data-testid="arena-battlefield-mana-value">
                {manaPet} / {manaCapPet}
              </span>
            </div>
            <div className="h-1.5 bg-black/70 border border-cyan-900/60 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all duration-500"
                style={{
                  width: `${Math.min(100, Math.max(0, Math.round((manaPet / Math.max(1, manaCapPet)) * 100)))}%`,
                }}
              />
            </div>
          </div>
          {/* Center clash spark */}
          {(petAttacking || cpuAttacking) && last && (
            <div
              key={`spark-${lastIdx}`}
              aria-hidden
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 arena-spark text-3xl font-display pointer-events-none"
              style={{ color: isCrit ? "#FCD34D" : "#DC2626" }}
            >
              {isCrit ? "💥 CRIT!" : "💢"}
            </div>
          )}
          {/* 💥 Crit drama — full-stage white flash on crit hits */}
          {isCrit && (petHit || cpuHit) && (
            <div key={`flash-${lastIdx}`} className="arena-crit-flash" aria-hidden />
          )}

          {/* 🎬 Ultimate cinema — fullscreen red bloom + ult-name banner */}
          <UltimateCutscene
            active={!!ultimate}
            ultName={ultimateMoveName}
            casterName={cpu.name}
          />

          {/* 🎬 Post-fight cinema — gold-rain on victory + glow/slump on fighters */}
          <PostFightCinema
            active={postFightActive}
            won={fight?.outcome === "pet"}
          />

          {/* 💤⚡ KO cooldown overlay — greyscale stage above, this card
              floats on top. Countdown ticks down from 30s. Player can
              pay 5 OD to skip. Feb 18 2026 owner tuning: skip CTA is
              now highlighted (amber gradient, larger, dot indicator)
              so users notice the paid-skip option. */}
          {koActive && (
            <div
              className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
              data-testid="arena-ko-overlay"
              style={{ filter: "none" }}
            >
              <div
                className="pointer-events-auto border-2 border-neutral-500 bg-black/85 backdrop-blur-sm px-5 py-4 text-center rounded-lg"
                style={{ minWidth: 280 }}
              >
                <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-400 font-bold mb-1">
                  💤 Knocked Out
                </div>
                <div
                  className="font-mono font-bold tabular-nums text-4xl text-neutral-200"
                  data-testid="arena-ko-timer"
                >
                  {koSecs}s
                </div>
                <div className="text-[10px] text-neutral-500 mt-1 mb-3">
                  Your pet wakes automatically when this hits 0.
                </div>

                {/* 🟡 Prominent paid-skip CTA — gradient + pulse dot + Ⓞ glyph
                    so the Omni-Dollar option reads as a real choice, not a
                    grey secondary tucked below the timer. */}
                <div className="text-[9px] uppercase tracking-[0.3em] text-amber-300/70 font-bold mb-1.5 flex items-center justify-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse"></span>
                  Skip the wait
                </div>
                <button
                  disabled={koSkipBusy}
                  onClick={onSkipKo}
                  data-testid="arena-ko-skip-btn"
                  className="w-full py-2.5 rounded-md border-2 border-amber-400 bg-gradient-to-b from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-black font-black uppercase tracking-widest text-[12px] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_18px_rgba(251,191,36,0.45)] hover:shadow-[0_0_28px_rgba(251,191,36,0.75)]"
                >
                  {koSkipBusy ? "Working…" : "⚡ Wake Now · Pay 5 Ⓞ OD"}
                </button>
                <div className="text-[9px] text-neutral-500 mt-1.5">
                  Costs <span className="text-amber-300 font-bold">5 Omni Dollars</span> · returns to full HP instantly
                </div>
              </div>
            </div>
          )}

          {/* 🎬 Pre-fight intro cutscene — name plates → VS → ROUND 1 FIGHT!
              Sits over the stage for the first ~2s of every fight. */}
          {!introDone && (
            <IntroCutscene
              petName={pet.name || "YOUR PET"}
              cpuName={cpu.name || "OPPONENT"}
              petTier={pet.tier}
              cpuTier={cpu.tier}
              cpuIsBoss={!!cpu.is_boss}
              onDone={() => setIntroDone(true)}
            />
          )}
        </div>

        {/* 🦸 Hero Skill Bar — Phase 2 — class identity + server-side Mana + real skill execution */}
        {!finished && awaitingMove && (
          <HeroSkillBar
            onPick={onMove}
            onSkill={onSkill}
            fightId={fight?.fight_id}
            serverMana={fight?.mana}
            serverManaCap={fight?.mana_cap}
          />
        )}

        {/* 🎮 Move Picker — appears between turns. Hidden when fight is over,
            during boss ult charging, or during pet KO (server still advances
            but player input is ignored). */}
        {!finished && awaitingMove && (
          <MovePicker
            onPick={onMove}
            last={last}
            fightId={fight?.fight_id}
            mana={manaPet ?? fight?.state?.mana ?? 0}
            manaCap={manaCapPet ?? fight?.state?.mana_cap ?? 100}
            skillCooldowns={fight?.state?.skill_cooldowns || {}}
            currentTurn={fight?.state?.turn ?? 0}
            onGlimmerFired={onGlimmerFired}
            data-testid="arena-move-picker"
          />
        )}

        {/* 🌍 Feb 10, 2026 — Community HP + top-5 leaderboard panel. */}
        {fight.is_boss && worldBossState && (
          <div
            className="border-t border-emerald-500/30 bg-emerald-950/20 px-4 py-2"
            data-testid="arena-community-panel"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold">
                🌍 Community HP
              </div>
              <div className="text-[10px] text-emerald-200 font-mono tabular-nums">
                {worldBossState.hp_current.toLocaleString()} / {worldBossState.hp_max.toLocaleString()}
                <span className="text-emerald-500 ml-2">
                  ({worldBossState.raider_count} raider{worldBossState.raider_count === 1 ? "" : "s"})
                </span>
              </div>
            </div>
            <div className="h-1.5 bg-neutral-900 border border-neutral-700 mb-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-[width] duration-500"
                style={{ width: `${Math.max(0, Math.min(100, (worldBossState.hp_current / Math.max(1, worldBossState.hp_max)) * 100))}%` }}
              />
            </div>
            {(worldBossState.top_contributors || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {(worldBossState.top_contributors || []).slice(0, 5).map((c) => (
                  <span
                    key={c.rank}
                    className="text-[10px] px-1.5 py-0.5 border border-emerald-500/40 bg-black/40 text-emerald-200 tabular-nums"
                    data-testid={`arena-leaderboard-rank-${c.rank}`}
                  >
                    <span className="text-emerald-400 font-bold">#{c.rank}</span>
                    {" "}{c.display || "Raider"}
                    <span className="text-neutral-500 ml-1">— {c.damage.toLocaleString()}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Battle log */}
        <div className="p-4 max-h-[36vh] overflow-y-auto relative" data-testid="arena-fight-log">
          {revealed.length === 0 && (
            <div className="text-center text-neutral-500 text-sm italic">⚔️ The bell rings…</div>
          )}
          <SfxMuteToggle />
          {!finished && revealed.length >= 2 && onFlee && !fight.is_boss && (
            <button
              onClick={onFlee}
              data-testid="arena-flee-mid-btn"
              className="sticky top-0 float-right ml-2 mb-1 bg-zinc-900/95 hover:bg-red-900 border border-red-800 text-red-300 hover:text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 transition"
              title="Forfeit the fight — no rewards, streak breaks"
            >
              🏳️ Flee
            </button>
          )}
          {revealed.map((line, i) => (
            <div
              key={i}
              data-testid={`arena-log-line-${i}`}
              className={`text-sm mb-1 px-2 py-1 ${
                line.type === "ultimate" ? "bg-red-900/60 border-l-4 border-[#DC2626] text-red-200 font-bold uppercase tracking-widest animate-pulse" :
                line.type === "charging" ? "bg-amber-900/40 border-l-4 border-[#FCD34D] text-[#FCD34D] font-bold" :
                line.type === "ko" ? "bg-purple-900/30 border-l-2 border-purple-700 text-purple-300 italic" :
                line.type === "wake" ? "bg-green-900/30 border-l-2 border-[#22C55E] text-[#22C55E] font-bold" :
                line.type === "handicap" ? "bg-amber-900/30 border-l-2 border-[#D4AF37] text-[#FCD34D] font-bold" :
                line.type === "stagger_apply" ? "bg-yellow-900/30 border-l-2 border-yellow-500 text-yellow-200 font-bold" :
                line.type === "stagger_skip" ? "bg-yellow-900/20 border-l-2 border-yellow-700 text-yellow-300 italic" :
                line.type === "burn" ? "bg-red-900/30 border-l-2 border-red-700 text-red-300" :
                line.blocked ? "bg-sky-900/40 border-l-4 border-[#38BDF8] text-sky-100 font-bold" :
                line.actor === "pet" ? "text-[#D4AF37]" : "text-red-300"
              } ${line.crit && line.type !== "ultimate" ? "font-bold animate-pulse" : ""}`}
              data-blocked={line.blocked ? "true" : undefined}
            >
              {line.blocked && <span className="mr-1">🛡️</span>}
              {line.msg}
              {line.blocked && line.heal > 0 && (
                <span className="ml-2 text-emerald-300">
                  · 🩹 +{line.heal} HP
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Result */}
        {finished && (
          <div className="border-t-2 border-[#D4AF37]/30 p-5" data-testid="arena-fight-result">
            <div className={`text-center text-3xl font-display mb-3 ${
              fight.outcome === "pet" ? "text-[#22C55E]" :
              fight.outcome === "draw" ? "text-[#D4AF37]" :
              fight.boss_ko_sleep ? "text-[#A78BFA]" : "text-[#DC2626]"
            }`}>
              {fight.outcome === "pet" ? "🏆 VICTORY" :
               fight.outcome === "draw" ? "🤝 DRAW" :
               fight.boss_ko_sleep ? "💤 KNOCKED OUT" : "💀 DEFEAT"}
            </div>
            {fight.boss_ko_sleep && (
              <div className="bg-purple-950/60 border-2 border-[#7C3AED] p-3 mb-3 text-center" data-testid="arena-boss-ko-sleep">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C4B5FD] font-black mb-1">
                  💤 Pet is taking a nap 💤
                </div>
                <div className="text-xs text-neutral-300 leading-snug">
                  <span className="text-white font-bold">{fight.cpu_stats?.name}</span> overwhelmed your pet — but bosses don't kill. Sleep ~30s and you'll wake up at <span className="text-[#22C55E] font-bold">FULL HP</span>.
                </div>
              </div>
            )}
            {fight.boss_escaped && (
              <div className="bg-red-950/60 border-2 border-[#DC2626] p-3 mb-3 text-center" data-testid="arena-boss-escaped">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FCA5A5] font-black mb-1">
                  🩸 Boss has successfully fled the battle 🩸
                </div>
                <div className="text-xs text-neutral-300 leading-snug">
                  Time elapsed and <span className="text-white font-bold">{fight.cpu_stats?.name}</span> was not killed.
                </div>
              </div>
            )}
            {fight.killing_blow && (
              <div
                className="text-center mb-3"
                data-testid="arena-killing-blow-badge"
              >
                {/* 🪙 Owner canon: no killing-blow celebration — a plain
                    informational chip is all we show here. The kill
                    appears in the world banner separately. */}
                <div className="inline-block bg-neutral-900 text-neutral-200 font-mono tracking-[0.2em] text-xs px-3 py-1 border border-neutral-700 uppercase">
                  Killing blow landed
                </div>
                {fight.killing_blow_reward && (
                  <div className="mt-2 text-xs text-neutral-400" data-testid="arena-killing-blow-bonus">
                    Bonus: <span className="font-bold text-[#FCD34D]">+{(fight.killing_blow_reward.chits || 0).toLocaleString()} 🪙 Chits</span>
                  </div>
                )}
              </div>
            )}
            {fight.pet_level_event?.leveled_up && (
              <div className="text-center mb-3" data-testid="arena-level-up-banner">
                <div className="inline-block bg-gradient-to-r from-emerald-600 via-amber-500 to-emerald-600 text-black font-display tracking-[0.2em] text-sm px-4 py-1 border-2 border-amber-300 animate-pulse">
                  🎉 LEVEL {fight.pet_level_event.after_level}!
                </div>
                <div className="mt-2 text-xs text-emerald-300 font-bold">
                  HP: <span className="font-mono tabular-nums">{fight.pet_level_event.max_hp_before}</span>
                  {" + "}
                  <span className="text-amber-300 font-mono tabular-nums">{fight.pet_level_event.hp_delta}</span>
                  {" = "}
                  <span className="text-emerald-200 font-mono tabular-nums">{fight.pet_level_event.max_hp_after}</span>
                </div>
                {fight.pet_level_event.levels_gained > 1 && (
                  <div className="text-[10px] text-amber-200 mt-1">
                    +{fight.pet_level_event.levels_gained} levels at once!
                  </div>
                )}
                <div className="text-[10px] text-neutral-400 mt-1">
                  ✨ Pet fully healed from level-up
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <RewardChip
                icon="🪙"
                label="Coins"
                value={`+${fight.rewards.battle_coins}`}
                testId="reward-coins"
              />
              <RewardChip
                icon={fight.rewards.feed_drop === "rainbow_milk" ? "🥛" : fight.rewards.feed_drop === "roasted_plant" ? "🍃" : "🫐"}
                label="Drop"
                value={fight.rewards.feed_drop ? "+1" : "—"}
                testId="reward-drop"
              />
              <RewardChip
                icon="⚡"
                label="XP"
                value={`+${fight.rewards.xp}`}
                testId="reward-xp"
              />
            </div>
            <div className="text-center text-xs text-neutral-400 mb-3">
              Win streak: <span className="text-[#D4AF37] font-bold font-mono tabular-nums" data-testid="reward-streak">{fight.win_streak}</span>
              {fight.rewards.streak_multiplier > 1 && (
                <span className="text-[#FCD34D] ml-2">({fight.rewards.streak_multiplier}x multiplier)</span>
              )}
            </div>

            {/* 🪙 Feb 10, 2026 — World Boss defeat consolation.
                Fires only when the pet was KO'd by a boss. */}
            {fight.rewards?.defeated_chits > 0 && (
              <div
                className="bg-amber-900/30 border-2 border-[#D4AF37]/60 p-3 mb-3 text-center"
                data-testid="arena-defeated-chits"
              >
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                  🪙 World Boss Consolation
                </div>
                <div className="text-sm text-white mt-1">
                  You dealt honorable damage. <span className="text-[#FCD34D] font-bold">+{fight.rewards.defeated_chits.toLocaleString()} Chits</span> credited.
                </div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  (10% of damage dealt)
                </div>
              </div>
            )}
            {(fight.boost_consumed || Number(fight.pet_stats?.raid_charm_level || 0) > 0) && (
              <div className="bg-purple-900/30 border border-[#7C3AED] p-2 mb-3 text-center" data-testid="arena-boost-applied">
                <span className="text-[10px] uppercase tracking-widest text-purple-300">
                  🔮 Power Charm · Lv {Number(fight.pet_stats?.raid_charm_level || 0)} · +{Number(fight.pet_stats?.raid_charm_pct || 0)}% all stats
                  {fight.boost_consumed ? ` · -${fight.boost_consumed.amount} ${fight.boost_consumed.currency === "pt" ? "Phoenix Tokens" : "OD"}` : ""}
                </span>
              </div>
            )}
            {fight.leak && (
              <div className="bg-cyan-900/30 border border-cyan-700 p-3 mb-3 text-center" data-testid="arena-leak">
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">👀 Preview Item Leak</div>
                <div className="text-sm text-white mt-1">
                  <span className="font-bold">{fight.leak.cpu_name}</span> was wearing <span className="text-cyan-300 font-bold">{fight.leak.name}</span>
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  {fight.leak.rarity} · {fight.leak.slot} · Coming in {fight.leak.season}
                </div>
              </div>
            )}
            <button
              onClick={onClose}
              data-testid="arena-close-fight"
              className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest py-3"
            >
              Back to arena
            </button>
            {/* Post-fight flee — CPU dummy fights only (bosses are commit-to-fight) */}
            {fight.win && onFlee && !fight.is_boss && (
              <button
                onClick={onFlee}
                data-testid="arena-flee-post-btn"
                className="w-full mt-1 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-red-400 py-1"
              >
                🏳️ Undo & flee · forfeit rewards · breaks streak
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FighterHeader({ stats, hp, pct, side, color }) {
  return (
    <div className={`flex items-center gap-2 ${side === "right" ? "flex-row-reverse text-right" : ""}`}>
      <div className="text-4xl">{stats.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold truncate" style={{ color }}>{stats.name}</div>
        <div className="text-[9px] text-neutral-500 truncate">{stats.tier_label}</div>
        <div className="mt-1 h-2 bg-black border border-zinc-800 overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${pct}%`, background: color }}
          />
        </div>
        <div className="text-[10px] font-mono tabular-nums text-neutral-400 mt-0.5">{Math.max(0, hp)} / {stats.max_hp}</div>
      </div>
    </div>
  );
}

function FighterStage({ stats, hp, pct, side, color, hit, attacking, crit, ko, charging, ultimateIncoming, ultimateUsed, staggered, staggeredSkip, dmg, animKey, banter, banterKey, charmLevel, charmPct, statusEffects, floats }) {
  const isLeft = side === "left";
  // Choose animation class for this frame. If nothing is happening (idle),
  // apply the gentle bob animation so the sprite feels alive.
  let animCls = "";
  const anyAction = hit || attacking || ko || charging || ultimateUsed || staggered || staggeredSkip;
  if (hit) animCls = crit ? "arena-hit-crit" : "arena-hit";
  else if (attacking) animCls = isLeft ? "arena-lunge-right" : "arena-lunge-left";
  else if (ko) animCls = "arena-ko";
  else if (charging) animCls = "arena-charge";
  else if (ultimateUsed) animCls = "arena-ult-cast";
  else if (staggered) animCls = "arena-stagger";
  else if (staggeredSkip) animCls = "arena-stagger-idle";
  else if (!anyAction) animCls = "arena-idle-bob";

  return (
    <div className={`flex flex-col items-center ${isLeft ? "" : ""}`} data-testid={`arena-stage-${side}`}>
      <div className="relative" style={{ width: 160, height: 160 }}>
        {/* ⚔️ Floating damage / heal numbers — red for damage, emerald for heals */}
        {(floats || []).map((f) => {
          const isHeal = f.delta > 0;
          const isCrit = !isHeal && Math.abs(f.delta) >= 100;
          const sign = isHeal ? "+" : "-";
          const color = isHeal
            ? "text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.9)]"
            : isCrit
              ? "text-amber-300 drop-shadow-[0_0_10px_rgba(252,211,77,0.95)]"
              : "text-rose-400 drop-shadow-[0_0_8px_rgba(244,63,94,0.85)]";
          return (
            <div
              key={f.id}
              data-testid={`arena-float-${side}-${f.id}`}
              className={`pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 z-30 font-black select-none tabular-nums ${
                isCrit ? "text-3xl" : "text-2xl"
              } ${color}`}
              style={{ animation: "embersweet-float 1.1s ease-out forwards" }}
            >
              {sign}{Math.abs(f.delta)}{isCrit ? " 💥" : isHeal ? " 🍓" : ""}
            </div>
          );
        })}
        {/* 🔮 Power Charm — floating tag above the pet sprite (raid bosses only) */}
        {charmLevel > 0 && (
          <div
            className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-[#4C1D95] via-[#7C3AED] to-[#4C1D95] border border-[#FCD34D] text-[#FCD34D] text-[10px] uppercase tracking-[0.15em] font-black px-2 py-0.5 leading-none shadow-[0_0_12px_#7C3AED]"
            data-testid={`arena-charm-tag-${side}`}
          >
            🔮 Lv {charmLevel} · +{charmPct}%
          </div>
        )}
        {/* 🗨️ Banter speech bubble — re-mounts on new key so animation replays */}
        {banter && (
          <div key={`banter-${banterKey}`} className="banter-bubble" data-testid={`arena-banter-${side}`}>
            {banter}
          </div>
        )}
        {/* Shadow under sprite */}
        <div
          aria-hidden
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-3 rounded-full opacity-50"
          style={{ background: "radial-gradient(ellipse, rgba(0,0,0,0.7) 0%, transparent 70%)" }}
        />
        {/* Sprite */}
        <div
          key={animKey}
          className={`absolute inset-0 flex items-center justify-center text-[110px] leading-none select-none ${animCls}`}
          style={{
            filter: ko ? "grayscale(80%) brightness(0.5)" :
                    ultimateIncoming ? `drop-shadow(0 0 30px ${color}) drop-shadow(0 0 60px ${color})` :
                    `drop-shadow(0 4px 8px ${color}66)`,
            // Note: inline transform omitted — the animation class handles
            // scaleX flipping (arena-idle-bob-flip / arena-lunge-right) for
            // the left-side player sprite. The inner <span> mirrors the
            // glyph so it faces the right way.
          }}
        >
          <span style={{ display: "inline-block" }}>
            {stats.icon}
          </span>
        </div>
        {/* Damage / crit floater — big, bold, with stroke for max readability */}
        {hit && dmg > 0 && (
          <div
            key={`dmg-${animKey}`}
            className="absolute inset-x-0 -top-2 text-center arena-floater pointer-events-none font-display font-black tabular-nums"
            style={{
              color: crit ? "#FCD34D" : "#FFFFFF",
              fontSize: crit ? "56px" : "42px",
              WebkitTextStroke: crit ? "2px #DC2626" : "2px #000",
              textShadow: crit
                ? "0 0 16px #FCD34D, 0 0 32px #DC2626, 0 4px 8px rgba(0,0,0,0.9)"
                : "0 0 8px #DC2626, 0 4px 8px rgba(0,0,0,0.9)",
              lineHeight: 1,
              zIndex: 10,
            }}
          >
            -{dmg}{crit ? "!" : ""}
          </div>
        )}
        {/* Charging halo */}
        {charging && (
          <div
            aria-hidden
            className="absolute inset-0 arena-charge-halo pointer-events-none"
            style={{
              borderRadius: "50%",
              boxShadow: `inset 0 0 30px ${color}, 0 0 30px ${color}`,
            }}
          />
        )}
      </div>
      {/* HP bar */}
      <div className="w-full max-w-[180px] mt-1">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-0.5">
          <span className="truncate font-bold" style={{ color }}>{stats.name}</span>
          <span className="font-mono tabular-nums text-neutral-400 shrink-0">{Math.max(0, hp)}/{stats.max_hp}</span>
        </div>
        <div className={`h-2.5 bg-black border border-zinc-800 overflow-hidden ${hit ? "arena-hp-shake" : ""}`}>
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{
              width: `${pct}%`,
              background: pct < 25 ? "#DC2626" : pct < 50 ? "#F59E0B" : "#22C55E",
            }}
          />
        </div>
        {/* 🌶️ Status Effect Pills */}
        {Array.isArray(statusEffects) && statusEffects.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1 justify-center" data-testid={`arena-status-pills-${side}`}>
            {statusEffects.map((e, i) => (
              <div
                key={`${e.kind}-${i}`}
                className="px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center gap-0.5 border"
                style={{
                  background: e.kind === "regen" || e.kind === "shield" ? "rgba(34,197,94,0.15)" : "rgba(220,38,38,0.15)",
                  borderColor: e.kind === "regen" || e.kind === "shield" ? "rgba(34,197,94,0.5)" : "rgba(220,38,38,0.5)",
                  color: e.kind === "regen" || e.kind === "shield" ? "#86efac" : "#fca5a5",
                }}
                title={`${e.label} · ${e.turns_left} turn(s) left${e.stacks > 1 ? ` · ${e.stacks}x` : ""}${e.dr_pct ? ` · -${e.dr_pct}% dmg` : ""}${e.dmg_per_tick ? ` · ${e.dmg_per_tick}/t` : ""}${e.heal_per_tick ? ` · +${e.heal_per_tick}/t` : ""}`}
                data-testid={`arena-status-${side}-${e.kind}`}
              >
                <span>{e.icon}</span>
                {e.stacks > 1 && <span className="font-mono">{e.stacks}x</span>}
                <span className="font-mono">{e.turns_left}t</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RewardChip({ icon, label, value, testId }) {
  return (
    <div className="bg-[#0F0F0F] border border-zinc-800 py-2" data-testid={testId}>
      <div className="text-xl">{icon}</div>
      <div className="font-mono tabular-nums text-base text-white">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

// 🔮 Raid Charm prompt — appears when clicking a raid boss. Shows the
// user's current charm level for THIS raid + buttons to stack one more
// level (up to MAX 5). Each level boosts ALL stats by 10% and persists
// for the entire 1-hour raid window. Disabled when at max.
function BoostPromptOverlay({ boss, boost, onCancel, onFight }) {
  const currentLvl = Number(boss?.my_raid_charm?.level || 0);
  const maxLvl = Number(boss?.my_raid_charm?.max_level || boost?.max_level || 5);
  const pctPerLevel = Number(boost?.pct_per_level || 10);
  const ptCost = Number(boost?.pt_cost || 200);
  const odCost = Number(boost?.od_cost || 2);
  const canAffordPt = !!boost?.can_afford_pt;
  const canAffordOd = !!boost?.can_afford_od;
  const atMax = currentLvl >= maxLvl;
  const nextLvl = Math.min(currentLvl + 1, maxLvl);
  const nextPct = nextLvl * pctPerLevel;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3" data-testid="arena-boost-prompt">
      <div className="bg-[#080808] border-2 border-[#7C3AED] max-w-md w-full p-6">
        <div className="text-center mb-4">
          <div className="text-5xl mb-2">{boss.icon}</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FCD34D] font-bold">{boss.difficulty_label} · Raid Boss</div>
          <div className="font-display text-2xl mt-1">{boss.name}</div>
          <div className="text-xs text-neutral-400 italic mt-1">"{boss.tagline}"</div>
        </div>

        <div className="bg-purple-900/25 border border-[#7C3AED] p-3 mb-4 text-center">
          <div className="text-[10px] uppercase tracking-widest text-[#C4B5FD] font-bold mb-1">🔮 Power Charm (Raid Bosses Only)</div>
          <div className="text-xs text-neutral-300 leading-snug">
            Each level boosts <span className="text-[#FCD34D] font-bold">ALL stats by +{pctPerLevel}%</span> (ATK · DEF · SPD · CRIT). Persists for the full 1-hour raid window — auto-clears when the boss is slain.
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[#A78BFA]">
            Current: <span className="text-white font-bold" data-testid="boost-current-lvl">🔮 Lv {currentLvl} / {maxLvl}</span>
            {!atMax && (
              <span className="ml-2 text-[#FCD34D]">→ Lv {nextLvl} · +{nextPct}% all stats</span>
            )}
            {atMax && <span className="ml-2 text-[#22C55E] font-bold">MAX</span>}
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <button
            onClick={() => onFight("pt")}
            disabled={atMax || !canAffordPt}
            data-testid="boost-pt-btn"
            className="w-full bg-[#7C3AED] hover:bg-[#9333EA] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-xs py-3 px-4 flex items-center justify-between"
          >
            <span>🔮 Stack Lv +1 · {ptCost} 🔥 Phoenix Tokens</span>
            <span className="text-[10px] opacity-80">{atMax ? "Max Lv" : canAffordPt ? "Available" : "Need Phoenix Tokens"}</span>
          </button>
          <button
            onClick={() => onFight("od")}
            disabled={atMax || !canAffordOd}
            data-testid="boost-od-btn"
            className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold uppercase tracking-widest text-xs py-3 px-4 flex items-center justify-between"
          >
            <span>🔮 Stack Lv +1 · {odCost} 🪙 OD</span>
            <span className="text-[10px] opacity-80">{atMax ? "Max Lv" : canAffordOd ? "Available" : "Need OD"}</span>
          </button>
          <button
            onClick={() => onFight(null)}
            data-testid="boost-skip-btn"
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-neutral-300 font-bold uppercase tracking-widest text-xs py-3 px-4 border border-zinc-800"
          >
            {currentLvl > 0
              ? `Fight with current 🔮 Lv ${currentLvl} (+${currentLvl * pctPerLevel}%)`
              : "Fight raw — no charm"}
          </button>
        </div>

        <button
          onClick={onCancel}
          data-testid="boost-cancel-btn"
          className="w-full text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white py-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// 🎮 MovePicker — between-turns picker. Renders the reusable SkillPicker
// (class-skill kit + mana bar + 25s auto-pick) as the primary picker, then
// appends the tiny Glimmer strip + Astral Hatching panel below.
// 🕰️ Retired 2026-03-01: the legacy Heavy/Light MoveButton pair. All
// combat picks now route through the class skill tree — pre-class users
// get a legacy Heavy/Light fallback rendered by SkillPicker itself.
function MovePicker({ onPick, last, fightId, mana, manaCap, skillCooldowns, currentTurn, onGlimmerFired }) {
  // ✅ Armed guard so the glimmer strip / astral hatch buttons still respect
  //    the one-pick-per-turn rule after SkillPicker fires. SkillPicker owns
  //    the primary timer/armed state internally; this local flag exists only
  //    so downstream buttons can gray out after a pick.
  const [armed, setArmed] = useState(true);
  const [glimmerState, setGlimmerState] = useState(null);
  const [glimmerBusy, setGlimmerBusy] = useState(false);
  // ⏱️ Per-glimmer fight-local meta — { [gid]: { firedAt, cd, usesUsed, usesMax } }
  const [glimmerMeta, setGlimmerMeta] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());
  // 💠 Astral Hatching — Phase 3 D-slice state
  const [astralState, setAstralState] = useState(null);
  const [astralBusy, setAstralBusy] = useState(false);
  const [vesperHatched, setVesperHatched] = useState(false);
  useEffect(() => {
    setArmed(true);
    const cdTick = setInterval(() => setNowTick(Date.now()), 500);
    axios.get(`${API}/glimmers/state`)
      .then((r) => setGlimmerState(r.data))
      .catch(() => setGlimmerState(null));
    axios.get(`${API}/astral/state`)
      .then((r) => setAstralState(r.data))
      .catch(() => setAstralState(null));
    return () => { clearInterval(cdTick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 🔄 Reset per-fight meta + Vesper flag when fight changes
  useEffect(() => { setGlimmerMeta({}); setVesperHatched(false); }, [fightId]);
  const pick = (m) => {
    if (!armed) return;
    setArmed(false);
    onPick(m);
  };
  // 💠 Astral Hatching — burn 1 coin to summon Vesper (Phase 3 D-slice stub).
  const hatchVesper = async () => {
    if (astralBusy || !fightId || vesperHatched) return;
    setAstralBusy(true);
    try {
      const r = await axios.post(`${API}/astral/hatch`, { fight_id: fightId });
      const v = r.data?.vesper || {};
      toast.success(
        `💠 Astral Hatching — ${v.name || "Vesper"} joins the fight! (${r.data?.balance_after ?? "?"} coins left)`,
        { duration: 5000 },
      );
      setVesperHatched(true);
      // Refresh local coin balance
      try {
        const s = await axios.get(`${API}/astral/state`);
        setAstralState(s.data);
      } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Astral Hatching failed.");
    } finally {
      setAstralBusy(false);
    }
  };

  const fireGlimmer = async (gid) => {
    if (glimmerBusy || !fightId) return;
    setGlimmerBusy(true);
    try {
      const r = await axios.post(`${API}/arena/use-glimmer`, {
        fight_id: fightId, glimmer_id: gid,
      });
      const eff = r.data.effect || {};
      let msg = `${eff.icon || "✨"} ${eff.name || gid} fired`;
      if (eff.heal_amount) msg += ` · +${eff.heal_amount} HP`;
      if (eff.dr_pct) msg += ` · ${eff.dr_pct}% DR × ${eff.turns}t`;
      if (eff.per_turn) msg += ` · 🩸 ${eff.per_turn}/turn × ${eff.turns}t`;
      if (eff.atk_bonus_pct) msg += ` · +${eff.atk_bonus_pct}% atk × ${eff.turns}t`;
      if (eff.cleansed?.length) msg += ` · cleansed ${eff.cleansed.join(", ")}`;
      toast.success(msg, { duration: 4500 });
      // 🩹 Feb 2026 bug fix — bubble the fresh HP state up to <Arena/>
      // so Nirvana / Mend heals paint the bar immediately (previously
      // the FE just showed the toast and waited for the next enemy tick).
      if (typeof onGlimmerFired === "function") {
        onGlimmerFired({
          pet_hp: r.data.state?.pet_hp,
          cpu_hp: r.data.state?.cpu_hp,
          heal_amount: eff.heal_amount,
          effect: eff,
        });
      }
      // Track fight-local CD + uses
      setGlimmerMeta((prev) => ({
        ...prev,
        [gid]: {
          firedAt: Date.now(),
          cd: r.data.cooldown_sec,
          usesUsed: r.data.uses_this_battle,
          usesMax: r.data.uses_per_battle,
        },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Glimmer failed.");
    } finally { setGlimmerBusy(false); }
  };
  const loadout = glimmerState?.loadout || [];
  // 🩹 Resolve from both active catalog AND legacy_owned so equipped
  // deprecated glimmers (e.g. legacy `rage`/`mend`) still appear in the
  // battle strip until backend migration swaps them for their successor.
  const loadoutDetails = loadout
    .map((gid) =>
      (glimmerState?.glimmers || []).find((x) => x.id === gid) ||
      (glimmerState?.legacy_owned || []).find((x) => x.id === gid)
    )
    .filter(Boolean);
  // Helper — compute live CD remaining and whether the slot is firable
  const cdInfo = (g) => {
    const m = glimmerMeta[g.id];
    if (!m) return { remaining: 0, usesUsed: 0, usesMax: g.uses_per_battle, locked: false };
    const elapsed = (nowTick - m.firedAt) / 1000;
    const remaining = Math.max(0, Math.ceil(m.cd - elapsed));
    const usedUp = m.usesUsed >= m.usesMax;
    return {
      remaining, usesUsed: m.usesUsed, usesMax: m.usesMax,
      locked: remaining > 0 || usedUp,
    };
  };
  return (
    <div className="bg-gradient-to-b from-black/80 to-[#0A0A0A] border-y border-[#D4AF37]/30 p-3" data-testid="arena-move-picker">
      {/* 💠 Primary picker: class-skill kit (or Heavy/Light for pre-class users) */}
      <SkillPicker
        fightId={fightId}
        mana={mana}
        manaCap={manaCap}
        skillCooldowns={skillCooldowns}
        currentTurn={currentTurn}
        onPick={pick}
        autoPickSecs={25}
      />
      {loadoutDetails.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 justify-center" data-testid="arena-glimmer-strip">
          <span className="text-[9px] uppercase tracking-widest text-fuchsia-400 font-bold">✨</span>
          {loadoutDetails.map((g) => {
            const cd = cdInfo(g);
            const disabled = glimmerBusy || !armed || cd.locked;
            return (
              <button
                key={g.id}
                data-testid={`arena-glimmer-btn-${g.id}`}
                disabled={disabled}
                onClick={() => fireGlimmer(g.id)}
                className={`relative px-2 py-1 rounded border text-[11px] font-bold flex items-center gap-1 transition ${
                  cd.locked
                    ? "border-neutral-700 bg-neutral-900/40 text-neutral-500"
                    : "border-fuchsia-500/40 bg-fuchsia-950/30 hover:bg-fuchsia-900/40 hover:border-fuchsia-400 text-fuchsia-200"
                } disabled:opacity-50`}
                title={`${g.name} · Lv ${g.level} · CD ${g.cooldown_sec}s · ×${g.uses_per_battle}`}
              >
                <span className="text-base leading-none">{g.icon}</span>
                <span>L{g.level}</span>
                {/* ⏱️ Live CD or uses pill */}
                {cd.remaining > 0 ? (
                  <span data-testid={`arena-glimmer-cd-${g.id}`}
                        className="absolute -top-1 -right-1 px-1 py-px rounded-full text-[8px] bg-neutral-800 text-amber-300 font-black tabular-nums leading-none">
                    {cd.remaining}s
                  </span>
                ) : cd.usesUsed > 0 ? (
                  <span data-testid={`arena-glimmer-uses-${g.id}`}
                        className={`absolute -top-1 -right-1 px-1 py-px rounded-full text-[8px] font-black leading-none ${
                          cd.usesUsed >= cd.usesMax ? "bg-rose-600 text-white" : "bg-fuchsia-500 text-white"
                        }`}>
                    {cd.usesUsed}/{cd.usesMax}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {/* 💠 Astral Hatching — Phase 3 D-slice (always-rendered hint when user has 0
          coins so they discover the system; active button when they have ≥1) */}
      {astralState && (
        <div className="mt-2 flex items-center justify-center gap-2" data-testid="arena-astral-strip">
          {astralState.balance >= 1 ? (
            <button
              data-testid="arena-astral-hatch-btn"
              disabled={astralBusy || !armed || vesperHatched}
              onClick={hatchVesper}
              title={vesperHatched
                ? "Vesper already on the field this fight."
                : `Burn 1 💠 Astral Coin to summon ${"Vesper"}. ${astralState.balance} in bag.`}
              className={`relative px-3 py-1.5 rounded border-2 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 transition ${
                vesperHatched
                  ? "border-blue-700/40 bg-blue-950/20 text-blue-300/60 cursor-default"
                  : "border-blue-400/60 bg-gradient-to-r from-blue-950/40 via-indigo-900/30 to-blue-950/40 hover:from-blue-900/60 hover:to-blue-900/60 hover:border-blue-300 text-blue-200 shadow-[0_0_10px_rgba(96,165,250,0.25)]"
              } disabled:opacity-50`}
            >
              <span className="text-base leading-none">💠</span>
              <span>{vesperHatched ? "Vesper Summoned" : "Astral Hatching"}</span>
              {!vesperHatched && (
                <span className="absolute -top-1 -right-1 px-1 py-px rounded-full text-[8px] font-black leading-none bg-blue-500 text-white tabular-nums">
                  {astralState.balance}
                </span>
              )}
            </button>
          ) : (
            <div
              data-testid="arena-astral-hint"
              title="Astral Coins are sold by the 🌌 Constellation Merchant (150 OD each, single-use)."
              className="text-[9px] uppercase tracking-widest text-blue-400/40 italic"
            >
              💠 Astral Coins unlock Vesper-summon (visit Constellation Merchant)
            </div>
          )}
          {/* 💙 Vesper on-field card — appears once she's hatched this fight */}
          {vesperHatched && (
            <div
              data-testid="arena-vesper-card"
              className="ml-1 px-2.5 py-1.5 rounded border-2 border-blue-400/60 bg-gradient-to-br from-blue-950/60 via-indigo-950/40 to-blue-950/60 flex items-center gap-2 shadow-[0_0_14px_rgba(96,165,250,0.35)] animate-pulse"
              title="Vesper the Eternal Crest — fighting at your side (basic attacks only in Phase 3 D-slice)"
            >
              <span className="text-2xl leading-none">💙</span>
              <div className="text-left">
                <div className="text-[10px] font-black uppercase tracking-wider text-blue-200 leading-tight">
                  Vesper
                </div>
                <div className="text-[9px] text-blue-300/80 leading-tight">
                  on the field
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MoveButton({ onClick, icon, label, hint, color, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="py-2.5 px-2 border-2 hover:bg-neutral-900 transition-all disabled:opacity-40 disabled:cursor-not-allowed font-bold"
      style={{ borderColor: color, color }}
    >
      <div className="text-2xl leading-none mb-1">{icon}</div>
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <div className="text-[9px] text-neutral-500 mt-0.5 normal-case font-normal">{hint}</div>
    </button>
  );
}

// ⚔️ PvP opponent card — shown in the quick-match grid.
function PvpOpponentCard({ opp, disabled, onChallenge }) {
  const handle = opp.handle ? `@${opp.handle}` : opp.display_name;
  return (
    <div
      data-testid={`pvp-opp-${opp.user_id}`}
      className="bg-[#0F0F0F] border border-[#7C3AED]/55 hover:border-[#7C3AED] p-3 flex flex-col text-center transition"
    >
      <div className="text-5xl mb-1">{opp.pet_icon}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#A78BFA] truncate" title={handle}>
        {handle}
      </div>
      <div className="font-display text-base leading-tight mb-1 truncate" title={opp.pet_name}>{opp.pet_name}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{opp.pet_tier.replace("_", " ")}</div>
      <div className="text-[10px] font-mono tabular-nums text-neutral-400 mb-2">
        HP {opp.pet_max_hp} · PvP {opp.pvp_wins}/{opp.pvp_losses}
      </div>
      {opp.best_streak > 0 && (
        <div className="text-[9px] uppercase tracking-widest text-[#FCD34D] mb-2" title="Best arena streak">
          🔥 best streak {opp.best_streak}
        </div>
      )}
      <button
        onClick={onChallenge}
        disabled={disabled}
        data-testid={`pvp-challenge-${opp.user_id}`}
        className="mt-auto px-3 py-1.5 text-xs uppercase tracking-widest font-bold bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 text-white"
      >
        Challenge
      </button>
    </div>
  );
}


// 🩹 Live HP regen ticker — shows "Next +10 HP in 2:43". When the timer
// reaches 0, calls onTick() so the Arena re-fetches the latest pet state.
function RegenTicker({ meta, onTick }) {
  const [secs, setSecs] = useState(meta.seconds_to_next_tick);
  useEffect(() => { setSecs(meta.seconds_to_next_tick); }, [meta.seconds_to_next_tick]);
  useEffect(() => {
    const id = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) {
          // Trigger backend refresh once we tick over to a new regen interval
          if (typeof onTick === "function") onTick();
          return meta.interval_seconds;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div
      className="text-[10px] uppercase tracking-[0.2em] text-[#22C55E] mt-2 text-center font-mono"
      data-testid="arena-hp-regen-ticker"
    >
      🩹 Next +{meta.amount} HP in {mm}:{ss}
    </div>
  );
}


// 🔊 Sticky mute toggle inside the battle log header
function SfxMuteToggle() {
  const [muted, setMuted] = useState(isMuted());
  return (
    <button
      onClick={() => {
        const next = toggleMuted();
        setMuted(next);
        if (!next) playSfx("hit_light");  // quick demo "on" feedback
      }}
      data-testid="arena-sfx-mute-btn"
      className="sticky top-0 float-left mr-2 mb-1 bg-zinc-900/95 hover:bg-zinc-800 border border-zinc-700 text-[10px] uppercase tracking-widest font-bold px-3 py-1 transition"
      title={muted ? "Sound off — click to enable" : "Sound on — click to mute"}
    >
      {muted ? "🔇 Muted" : "🔊 SFX"}
    </button>
  );
}


// 🐉 RaidPanel — shared HP bar + top-3 leaderboard for a timed boss raid.
function RaidPanel({ raid, bossId, bossColor }) {
  const pct = Math.max(0, Math.round((raid.current_hp / raid.starting_hp) * 100));
  const isAlive = raid.status === "alive";
  const isKilled = raid.status === "killed";
  const top = (raid.leaderboard || []).slice(0, 3);
  return (
    <div
      className="mb-2 w-full text-left bg-black/40 border border-neutral-800 p-2"
      data-testid={`raid-panel-${bossId}`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: isAlive ? bossColor : "#A1A1AA" }}>
          {isAlive ? "🐉 World Boss" : isKilled ? "💀 SLAIN" : "🪦 Bled Out"}
        </span>
        <span className="text-[9px] font-mono tabular-nums text-neutral-400">
          {raid.current_hp.toLocaleString()} / {raid.starting_hp.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-neutral-900 border border-neutral-800 overflow-hidden">
        <div
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            background: isAlive ? bossColor : "#525252",
          }}
          data-testid={`raid-hp-bar-${bossId}`}
        />
      </div>
      {raid.participant_count > 0 && (
        <div className="text-[8px] uppercase tracking-widest text-neutral-500 mt-1">
          {raid.participant_count} hunter{raid.participant_count !== 1 ? "s" : ""} engaged
        </div>
      )}
      {isKilled && raid.killed_by_display && (
        <div className="text-[9px] uppercase tracking-[0.15em] text-[#FCD34D] mt-1 font-bold" data-testid={`raid-killed-by-${bossId}`}>
          🗡️ Killing blow: {raid.killed_by_display}
        </div>
      )}
      {top.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {top.map((p, i) => (
            <div key={p.user_id} className="flex items-center justify-between text-[9px] font-mono">
              <span className="text-neutral-500 truncate">
                {i === 0 ? "🥇 " : i === 1 ? "🥈 " : "🥉 "}{p.display_name || (p.handle ? `@${p.handle}` : "Phoenix")}
              </span>
              <span className="text-neutral-400 tabular-nums">{(p.damage_dealt || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

